/**
 * VLearn Tuition Centre — Frontend v2
 * Admin: santha@123 / 181998
 * All records saved to backend · Real-time Socket.io
 */
import { useState, useEffect, useCallback } from "react";
import { api, connectSocket, disconnectSocket } from "./api";

/* ══════ LOGOS ══════ */
function VLearnLogo({ size = 120, rounded = false, bg = true }) {
  const u = "l" + size + (rounded ? "r" : "") + (bg ? "b" : "");
  return (
    <svg width={size} height={size} viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={"bg"+u} x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#0d1f3c"/><stop offset="100%" stopColor="#1a3a70"/></linearGradient>
        <linearGradient id={"g"+u} x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#ffd050"/><stop offset="50%" stopColor="#f5a800"/><stop offset="100%" stopColor="#e07800"/></linearGradient>
        <linearGradient id={"t"+u} x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#5ef5ee"/><stop offset="100%" stopColor="#22d3c8"/></linearGradient>
        <linearGradient id={"w"+u} x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#fff"/><stop offset="100%" stopColor="#dde6f5"/></linearGradient>
        <filter id={"s"+u}><feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="rgba(0,0,0,0.4)"/></filter>
        <clipPath id={"c"+u}><rect width="512" height="512" rx={rounded?112:0}/></clipPath>
      </defs>
      <g clipPath={bg?"url(#c"+u+")":undefined}>
        {bg&&<rect width="512" height="512" rx={rounded?112:0} fill={"url(#bg"+u+")"}/>}
        <path d="M120 310 Q170 285 256 295 L256 415 Q170 408 120 430Z" fill="none" stroke={"url(#t"+u+")"} strokeWidth="12" strokeLinejoin="round"/>
        <path d="M392 310 Q342 285 256 295 L256 415 Q342 408 392 430Z" fill="none" stroke={"url(#t"+u+")"} strokeWidth="12" strokeLinejoin="round"/>
        <line x1="256" y1="295" x2="256" y2="415" stroke={"url(#t"+u+")"} strokeWidth="10"/>
        <line x1="130" y1="95" x2="256" y2="285" stroke={"url(#g"+u+")"} strokeWidth="52" strokeLinecap="round" filter={"url(#s"+u+")"}/>
        <line x1="382" y1="95" x2="256" y2="285" stroke={"url(#g"+u+")"} strokeWidth="52" strokeLinecap="round" filter={"url(#s"+u+")"}/>
        <text x="256" y="490" textAnchor="middle" fontFamily="Nunito,sans-serif" fontWeight="900" fontSize="62" fill={"url(#w"+u+")"} letterSpacing="8">LEARN</text>
      </g>
    </svg>
  );
}
function VLearnLogoH({ height = 60 }) {
  return (
    <svg width={height*4.2} height={height} viewBox="0 0 420 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="hlg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#ffd050"/><stop offset="100%" stopColor="#f5a800"/></linearGradient>
        <linearGradient id="htg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#5ef5ee"/><stop offset="100%" stopColor="#22d3c8"/></linearGradient>
      </defs>
      <circle cx="50" cy="50" r="46" fill="#0d1f3c" stroke="rgba(245,168,0,0.3)" strokeWidth="2"/>
      <line x1="22" y1="20" x2="50" y2="65" stroke="url(#hlg)" strokeWidth="10" strokeLinecap="round"/>
      <line x1="78" y1="20" x2="50" y2="65" stroke="url(#hlg)" strokeWidth="10" strokeLinecap="round"/>
      <text x="110" y="58" fontFamily="Nunito,sans-serif" fontWeight="900" fontSize="52" fill="url(#hlg)">V</text>
      <text x="147" y="56" fontFamily="Nunito,sans-serif" fontWeight="900" fontSize="38" fill="#ffffff" letterSpacing="2">LEARN</text>
      <text x="147" y="74" fontFamily="DM Sans,sans-serif" fontWeight="500" fontSize="13" fill="#7b9ccc" letterSpacing="3">TUITION CENTRE</text>
      <rect x="147" y="78" width="180" height="2.5" rx="1.5" fill="url(#hlg)" opacity="0.6"/>
    </svg>
  );
}

/* ══════ CONSTANTS ══════ */
const GRADES=["LKG","UKG","Class 1","Class 2","Class 3","Class 4","Class 5","Class 6","Class 7","Class 8","Class 9","Class 10"];
const ALL_SUBJECTS=["All Subjects","Mathematics","English","Science","Social Studies","Maths+Science"];
const NOTICE_TAGS=["Exam Alert","Holiday","Leave","Fee Reminder","New Batch","Result","General","Event"];
const tagColor={"Exam Alert":"#f4546a","Holiday":"#f5a800","Leave":"#a78bfa","Fee Reminder":"#4f8ef7","New Batch":"#22d3c8","Result":"#22c55e","General":"#7b9ccc","Event":"#ffd050"};
const SCHEDULE=[{time:"8:00 AM",name:"Mathematics",meta:"Class 4 & 5",room:"Room A",color:"#f5a800"},{time:"9:30 AM",name:"English",meta:"Class 3",room:"Room B",color:"#22d3c8"},{time:"11:00 AM",name:"Science",meta:"Class 8 & 9",room:"Room A",color:"#4f8ef7"},{time:"2:00 PM",name:"All Subjects",meta:"LKG–Class 2",room:"Room C",color:"#f4546a"},{time:"3:30 PM",name:"Maths+Science",meta:"Class 10",room:"Room A",color:"#a78bfa"}];

/* ══════ HELPERS ══════ */
const AVC=["av1","av2","av3","av4","av5"];
const getAv=i=>AVC[i%5];
const ini=n=>n.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2);
const feeClr=f=>f==="Paid"?"bg3":f==="Pending"?"bo":"br3";
const attGrd=a=>a>=85?"linear-gradient(90deg,#22d3c8,#5ef5ee)":a>=75?"linear-gradient(90deg,#f5a800,#ffd050)":"linear-gradient(90deg,#f4546a,#f89ba5)";
const attClr=a=>a>=85?"#22d3c8":a>=75?"#f5a800":"#f4546a";
const todayStr=()=>new Date().toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"});

function useToasts(){
  const [toasts,setToasts]=useState([]);
  const add=useCallback((msg,type="success",icon="✓")=>{
    const id=Date.now();
    setToasts(p=>[...p,{id,msg,type,icon}]);
    setTimeout(()=>setToasts(p=>p.filter(t=>t.id!==id)),3500);
  },[]);
  return{toasts,addToast:add};
}

/* ══════ CSS ══════ */
const CSS=`
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@600;700;800;900&family=DM+Sans:wght@300;400;500;600&display=swap');
*{margin:0;padding:0;box-sizing:border-box;}
:root{--bg:#07112a;--bg2:#0c1d3e;--bg3:#112450;--card:#0f2040;--card2:#142850;--gold:#f5a800;--gold2:#ffd050;--gf:rgba(245,168,0,.12);--teal:#22d3c8;--rose:#f4546a;--blue:#4f8ef7;--purple:#a78bfa;--green:#22c55e;--w:#fff;--t1:#dde6f5;--t2:#7b9ccc;--t3:#4a6899;--bdr:rgba(255,255,255,.07);--sb-w:252px;}
body{font-family:'DM Sans',sans-serif;background:var(--bg);color:var(--t1);min-height:100vh;}
@keyframes fadeUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
@keyframes pop{0%{transform:scale(.9);opacity:0}100%{transform:scale(1);opacity:1}}
@keyframes pulse2{0%,100%{box-shadow:0 0 0 0 rgba(245,168,0,.5)}50%{box-shadow:0 0 0 10px rgba(245,168,0,0)}}
@keyframes slideIn{from{transform:translateX(120%);opacity:0}to{transform:translateX(0);opacity:1}}
@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
.fu{animation:fadeUp .32s ease both}.fu2{animation:fadeUp .32s .08s ease both}.fu3{animation:fadeUp .32s .16s ease both}
.spin{animation:spin .7s linear infinite}

.shell{display:flex;min-height:100vh;}
.main{margin-left:var(--sb-w);flex:1;display:flex;flex-direction:column;}
.content{padding:20px 26px;flex:1;}

/* SIDEBAR */
.sidebar{width:var(--sb-w);background:linear-gradient(180deg,#050e22,#0a1b38);border-right:1px solid var(--bdr);display:flex;flex-direction:column;position:fixed;height:100vh;z-index:100;box-shadow:4px 0 28px rgba(0,0,0,.45);transition:transform .25s;}
.sb-logo{padding:14px 16px 12px;border-bottom:1px solid var(--bdr);background:linear-gradient(135deg,rgba(245,168,0,.08),transparent);}
.sb-sub{font-size:.62rem;color:var(--t2);margin-top:5px;}
.grow{margin-top:6px;display:flex;align-items:center;gap:5px;}
.gpill{background:rgba(245,168,0,.13);border:1px solid rgba(245,168,0,.28);color:var(--gold);font-size:.59rem;font-weight:700;padding:2px 8px;border-radius:20px;}
.rband{margin:8px 14px;padding:9px 12px;border-radius:10px;display:flex;align-items:center;gap:9px;}
.rband.admin{background:rgba(245,168,0,.08);border:1px solid rgba(245,168,0,.18);}
.rband.teacher{background:rgba(79,142,247,.08);border:1px solid rgba(79,142,247,.18);}
.rband.parent{background:rgba(34,211,200,.08);border:1px solid rgba(34,211,200,.18);}
.rbn{font-size:.8rem;font-weight:600;color:var(--w);}
.rbr{font-size:.62rem;color:var(--t2);}
.sb-nav{padding:6px 10px;flex:1;overflow-y:auto;}
.nsec{margin-bottom:14px;}
.nlbl{font-size:.57rem;text-transform:uppercase;letter-spacing:.13em;color:var(--t3);padding:0 10px;margin-bottom:4px;font-weight:600;}
.ni{display:flex;align-items:center;gap:9px;padding:9px 11px;border-radius:10px;font-size:.82rem;font-weight:500;color:var(--t2);cursor:pointer;transition:all .18s;margin-bottom:1px;}
.ni:hover{background:rgba(255,255,255,.05);color:var(--w);}
.ni.active{background:linear-gradient(135deg,rgba(245,168,0,.18),rgba(245,168,0,.05));color:var(--gold);border:1px solid rgba(245,168,0,.2);}
.ni .ic{font-size:.9rem;width:30px;height:30px;display:flex;align-items:center;justify-content:center;border-radius:8px;font-weight:700;transition:all .18s;flex-shrink:0;box-shadow:0 2px 8px rgba(0,0,0,.25);}
.ni.active .ic{transform:scale(1.08);box-shadow:0 3px 12px rgba(0,0,0,.35);}
.ni:hover .ic{transform:scale(1.05);}
.nbadge{margin-left:auto;background:var(--rose);color:#fff;font-size:.57rem;font-weight:800;padding:1px 6px;border-radius:20px;min-width:18px;text-align:center;}
.sb-foot{padding:10px;border-top:1px solid var(--bdr);}
@media(max-width:768px){.sb-foot{padding-bottom:80px;}}
.cbox{background:linear-gradient(135deg,var(--bg3),var(--card2));border:1px solid rgba(245,168,0,.18);border-radius:11px;padding:10px 12px;}
.cbox .clbl{font-size:.58rem;color:var(--t2);text-transform:uppercase;margin-bottom:2px;}
.cbox .cph{font-size:.87rem;font-weight:800;color:var(--gold);}
.cbox .cadr{font-size:.59rem;color:var(--t2);margin-top:3px;line-height:1.5;}
.lobtn{width:100%;margin-top:8px;padding:7px;background:rgba(244,84,106,.08);border:1px solid rgba(244,84,106,.18);border-radius:9px;color:var(--rose);font-family:'DM Sans',sans-serif;font-size:.77rem;font-weight:600;cursor:pointer;transition:all .18s;}
.lobtn:hover{background:rgba(244,84,106,.16);}

/* BOTTOM TABS */
.bottom-tabs{display:none;position:fixed;bottom:0;left:0;right:0;z-index:200;background:linear-gradient(180deg,#050e22,#030c1e);border-top:1px solid var(--bdr);padding:6px 0 env(safe-area-inset-bottom,6px);box-shadow:0 -4px 20px rgba(0,0,0,.5);}
.btab-item{flex:1;display:flex;flex-direction:column;align-items:center;gap:3px;padding:6px 4px;cursor:pointer;transition:all .18s;position:relative;background:none;border:none;color:var(--t3);font-family:'DM Sans',sans-serif;}
.btab-item.active{color:var(--gold);}
.btab-icon{font-size:1.05rem;line-height:1;width:34px;height:34px;display:flex;align-items:center;justify-content:center;border-radius:10px;margin:0 auto 3px;transition:all .22s;font-weight:700;box-shadow:0 2px 8px rgba(0,0,0,.2);}
.btab-item.active .btab-icon{transform:scale(1.12);box-shadow:0 4px 14px rgba(0,0,0,.3);}
.btab-item.active .btab-label{color:var(--gold);}
.btab-label{font-size:.58rem;font-weight:600;}
.btab-badge{position:absolute;top:4px;right:calc(50% - 18px);background:var(--rose);color:#fff;font-size:.5rem;font-weight:800;padding:1px 4px;border-radius:10px;min-width:14px;text-align:center;}
.sb-overlay{display:none;position:fixed;inset:0;background:rgba(0,0,0,.65);z-index:150;backdrop-filter:blur(3px);}
.sb-overlay.open{display:block;}

/* TOPBAR */
.topbar{padding:12px 24px;border-bottom:1px solid var(--bdr);display:flex;align-items:center;justify-content:space-between;background:rgba(5,14,34,.96);backdrop-filter:blur(10px);position:sticky;top:0;z-index:50;}
.tb-t h2{font-family:'Nunito',sans-serif;font-size:1.2rem;font-weight:800;color:var(--w);}
.tb-t p{font-size:.67rem;color:var(--t2);margin-top:1px;display:flex;align-items:center;gap:4px;}
.tb-a{display:flex;gap:9px;align-items:center;}
.menu-btn{display:none;background:rgba(255,255,255,.05);border:1px solid var(--bdr);border-radius:9px;padding:8px 10px;cursor:pointer;font-size:1.1rem;color:var(--t1);}
.live-dot{width:7px;height:7px;border-radius:50%;background:var(--teal);display:inline-block;animation:pulse2 1.8s infinite;flex-shrink:0;}

/* BUTTONS */
.btn{padding:8px 16px;border-radius:9px;border:none;font-family:'DM Sans',sans-serif;font-size:.8rem;font-weight:600;cursor:pointer;transition:all .18s;display:inline-flex;align-items:center;gap:6px;}
.bp{background:linear-gradient(135deg,var(--gold),var(--gold2));color:#050e22;box-shadow:0 4px 14px rgba(245,168,0,.26);}
.bp:hover{transform:translateY(-1px);}
.bp:disabled{opacity:.6;cursor:not-allowed;transform:none;}
.bg2{background:rgba(255,255,255,.05);color:var(--t1);border:1px solid var(--bdr);}
.bg2:hover{border-color:var(--gold);color:var(--gold);}
.br2{background:rgba(244,84,106,.1);color:var(--rose);border:1px solid rgba(244,84,106,.22);}
.bt{background:rgba(34,211,200,.1);color:var(--teal);border:1px solid rgba(34,211,200,.22);}
.bgr{background:rgba(34,197,94,.1);color:var(--green);border:1px solid rgba(34,197,94,.22);}
.bbl{background:rgba(79,142,247,.1);color:var(--blue);border:1px solid rgba(79,142,247,.22);}

/* CARDS */
.card{background:var(--card);border:1px solid var(--bdr);border-radius:14px;overflow:hidden;}
.ch{padding:13px 18px 11px;border-bottom:1px solid var(--bdr);display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;}
.ct{font-family:'Nunito',sans-serif;font-size:.93rem;font-weight:800;color:var(--w);}
.cb{padding:14px 18px;}

/* STAT CARDS */
.sg{display:grid;gap:12px;margin-bottom:20px;}
.sg4{grid-template-columns:repeat(4,1fr);}
.sg3{grid-template-columns:repeat(3,1fr);}
.sc{background:var(--card);border:1px solid var(--bdr);border-radius:14px;padding:16px 18px;position:relative;overflow:hidden;}
.sc::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;border-radius:14px 14px 0 0;}
.c1::before{background:linear-gradient(90deg,var(--gold),var(--gold2));}
.c2::before{background:linear-gradient(90deg,var(--teal),#5ef5ee);}
.c3::before{background:linear-gradient(90deg,var(--blue),#82b0ff);}
.c4::before{background:linear-gradient(90deg,var(--rose),#f89ba5);}
.si{position:absolute;top:13px;right:13px;width:36px;height:36px;border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:1rem;}
.c1 .si{background:rgba(245,168,0,.14);}.c2 .si{background:rgba(34,211,200,.14);}.c3 .si{background:rgba(79,142,247,.14);}.c4 .si{background:rgba(244,84,106,.14);}
.slbl{font-size:.63rem;text-transform:uppercase;letter-spacing:.09em;color:var(--t2);margin-bottom:6px;font-weight:600;}
.sval{font-family:'Nunito',sans-serif;font-size:1.8rem;font-weight:900;color:var(--w);line-height:1;}
.ssub{font-size:.66rem;color:var(--t2);margin-top:4px;}
.up{color:var(--teal);}.dn{color:var(--rose);}

/* GRIDS */
.g2{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:16px;}
.g3{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;margin-bottom:16px;}
.gm{display:grid;grid-template-columns:1.7fr 1fr;gap:16px;margin-bottom:16px;}

/* TABLE */
.tbl-wrap{overflow-x:auto;-webkit-overflow-scrolling:touch;}
.tbl{width:100%;border-collapse:collapse;min-width:480px;}
.tbl th{text-align:left;font-size:.61rem;text-transform:uppercase;letter-spacing:.1em;color:var(--t2);font-weight:600;padding:9px 12px;border-bottom:1px solid var(--bdr);}
.tbl td{padding:9px 12px;font-size:.81rem;border-bottom:1px solid rgba(255,255,255,.04);vertical-align:middle;}
.tbl tr:last-child td{border-bottom:none;}
.tbl tr:hover td{background:rgba(245,168,0,.025);}

/* BADGES */
.bd{display:inline-block;padding:2px 9px;border-radius:20px;font-size:.63rem;font-weight:700;}
.bg3{background:rgba(34,211,200,.13);color:var(--teal);}
.br3{background:rgba(244,84,106,.13);color:var(--rose);}
.bo{background:rgba(245,168,0,.13);color:var(--gold);}
.bb{background:rgba(79,142,247,.13);color:var(--blue);}
.bpu{background:rgba(167,139,250,.13);color:var(--purple);}
.bgr2{background:rgba(34,197,94,.13);color:var(--green);}

/* AVATAR */
.av{width:30px;height:30px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;font-size:.67rem;font-weight:700;flex-shrink:0;}
.av1{background:linear-gradient(135deg,var(--gold),var(--gold2));color:#050e22;}
.av2{background:linear-gradient(135deg,var(--teal),#5ef5ee);color:#050e22;}
.av3{background:linear-gradient(135deg,var(--blue),#82b0ff);color:#050e22;}
.av4{background:linear-gradient(135deg,var(--rose),#f89ba5);color:#050e22;}
.av5{background:linear-gradient(135deg,var(--purple),#c4b5fd);color:#050e22;}
.nc{display:flex;align-items:center;gap:8px;}

/* PROGRESS */
.pb{background:rgba(255,255,255,.07);border-radius:4px;height:5px;overflow:hidden;margin-top:4px;}
.pf{height:100%;border-radius:4px;}

/* FORM */
.fg{margin-bottom:13px;}
.fl{font-size:.67rem;text-transform:uppercase;letter-spacing:.08em;color:var(--t2);font-weight:600;display:block;margin-bottom:5px;}
.fi{width:100%;padding:10px 13px;background:rgba(255,255,255,.05);border:1px solid var(--bdr);border-radius:9px;color:var(--w);font-family:'DM Sans',sans-serif;font-size:.85rem;outline:none;transition:border-color .18s,background .18s;}
.fi:focus{border-color:var(--gold);background:rgba(245,168,0,.04);}
select.fi{appearance:none;cursor:pointer;}
textarea.fi{resize:vertical;}
.fr2{display:grid;grid-template-columns:1fr 1fr;gap:11px;}

/* MODAL */
.ov{position:fixed;inset:0;background:rgba(0,0,0,.78);z-index:200;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(6px);padding:20px;}
.modal{background:var(--bg2);border:1px solid var(--bdr);border-radius:18px;padding:26px;width:560px;max-width:100%;box-shadow:0 24px 60px rgba(0,0,0,.6);max-height:92vh;overflow-y:auto;animation:pop .22s ease;}
.mh{display:flex;align-items:center;justify-content:space-between;margin-bottom:20px;}
.mh h3{font-family:'Nunito',sans-serif;font-size:1.1rem;font-weight:800;color:var(--w);}
.cls{background:none;border:none;color:var(--t2);font-size:1.1rem;cursor:pointer;padding:4px;}
.ma{display:flex;gap:10px;margin-top:18px;justify-content:flex-end;}

/* TABS */
.chip{display:inline-flex;align-items:center;padding:2px 8px;background:rgba(255,255,255,.06);border-radius:20px;font-size:.71rem;color:var(--t2);}
.tabs{display:flex;gap:4px;background:rgba(255,255,255,.04);border-radius:10px;padding:4px;margin-bottom:16px;overflow-x:auto;}
.tab{flex:1;padding:7px 10px;border-radius:7px;border:none;font-family:'DM Sans',sans-serif;font-size:.77rem;font-weight:500;cursor:pointer;transition:all .18s;color:var(--t2);background:transparent;text-transform:capitalize;white-space:nowrap;}
.tab.active{background:var(--card);color:var(--gold);}

/* TOAST */
.toast-wrap{position:fixed;bottom:80px;right:16px;z-index:300;display:flex;flex-direction:column;gap:8px;align-items:flex-end;}
.toast{padding:11px 18px;border-radius:11px;font-weight:700;font-size:.82rem;animation:slideIn .3s ease;box-shadow:0 8px 24px rgba(0,0,0,.3);display:flex;align-items:center;gap:8px;max-width:300px;}
.toast.success{background:var(--teal);color:#050e22;}
.toast.msg{background:linear-gradient(135deg,#1a3a70,#0f2550);border:1px solid rgba(245,168,0,.3);color:var(--w);}
.toast.mark{background:linear-gradient(135deg,#22c55e,#16a34a);color:#fff;}
.toast.err{background:var(--rose);color:#fff;}

/* ATTENDANCE */
.att-row{display:flex;align-items:center;gap:12px;padding:11px 0;border-bottom:1px solid rgba(255,255,255,.05);}
.att-row:last-child{border-bottom:none;}
.att-btn{padding:5px 10px;border-radius:8px;border:1px solid var(--bdr);background:rgba(255,255,255,.04);font-family:'DM Sans',sans-serif;font-size:.7rem;font-weight:600;cursor:pointer;transition:all .18s;color:var(--t2);}
.att-p-active{border-color:var(--teal)!important;color:var(--teal)!important;background:rgba(34,211,200,.1)!important;}
.att-a-active{border-color:var(--rose)!important;color:var(--rose)!important;background:rgba(244,84,106,.1)!important;}
.att-l-active{border-color:var(--gold)!important;color:var(--gold)!important;background:rgba(245,168,0,.1)!important;}
.att-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:4px;}
.att-d{aspect-ratio:1;border-radius:6px;display:flex;align-items:center;justify-content:center;font-size:.64rem;font-weight:600;}
.att-p{background:rgba(34,211,200,.18);color:var(--teal);}
.att-a{background:rgba(244,84,106,.18);color:var(--rose);}
.att-e{background:rgba(255,255,255,.04);color:transparent;}

/* MARKS */
.marks-card{background:var(--card);border:1px solid var(--bdr);border-radius:12px;padding:16px;margin-bottom:10px;}
.marks-score{font-family:'Nunito',sans-serif;font-size:1.8rem;font-weight:900;}
.marks-bar{background:rgba(255,255,255,.07);border-radius:6px;height:8px;overflow:hidden;margin-top:6px;}
.marks-fill{height:100%;border-radius:6px;transition:width .6s ease;}
.remark-box{background:rgba(245,168,0,.06);border:1px solid rgba(245,168,0,.15);border-radius:9px;padding:9px 12px;margin-top:8px;font-size:.78rem;color:var(--t1);font-style:italic;}

/* HOME */
.hero-banner{background:linear-gradient(135deg,#0f2550,#1a3a70);border:1px solid rgba(245,168,0,.2);border-radius:16px;padding:28px;margin-bottom:20px;position:relative;overflow:hidden;text-align:center;}
.hero-banner h1{font-family:'Nunito',sans-serif;font-size:1.6rem;font-weight:900;color:var(--w);margin-bottom:4px;}
.hero-banner h1 span{color:var(--gold);}
.hero-banner .sub{font-family:'Nunito',sans-serif;font-size:1.1rem;font-weight:700;color:var(--gold2);margin-bottom:10px;}
.gallery{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:16px;}
.gimg{position:relative;aspect-ratio:4/3;border-radius:12px;overflow:hidden;border:1px solid var(--bdr);}
.gimg img{width:100%;height:100%;object-fit:cover;transition:transform .3s;}
.gimg:hover img{transform:scale(1.05);}
.gimg .gcap{position:absolute;bottom:0;left:0;right:0;background:linear-gradient(transparent,rgba(0,0,0,.7));padding:8px 10px;font-size:.76rem;font-weight:600;color:#fff;}
.gimg .gdel{position:absolute;top:8px;right:8px;background:rgba(244,84,106,.8);border:none;border-radius:6px;color:#fff;width:26px;height:26px;cursor:pointer;font-size:.8rem;display:flex;align-items:center;justify-content:center;}
.notice-card{border-radius:12px;padding:14px 16px;margin-bottom:10px;border:1px solid rgba(255,255,255,.07);position:relative;overflow:hidden;}
.map-card{background:var(--card);border:1px solid var(--bdr);border-radius:14px;overflow:hidden;margin-bottom:16px;}
.map-frame{width:100%;height:260px;border:none;}
.sched-item{display:flex;align-items:center;gap:10px;padding:9px 0;border-bottom:1px solid rgba(255,255,255,.05);}
.sched-item:last-child{border-bottom:none;}

/* AUTH */
.auth-wrap{min-height:100vh;display:flex;align-items:center;justify-content:center;background:radial-gradient(ellipse at 20% 50%,rgba(245,168,0,.07),transparent 55%),radial-gradient(ellipse at 80% 20%,rgba(79,142,247,.07),transparent 55%),var(--bg);padding:20px;}
.auth-box{width:100%;max-width:460px;}
.auth-logo{text-align:center;margin-bottom:28px;}
.gstrip{display:inline-flex;align-items:center;gap:7px;margin-top:9px;background:var(--gf);border:1px solid rgba(245,168,0,.22);border-radius:20px;padding:4px 14px;}
.gstrip span{font-size:.69rem;font-weight:700;color:var(--gold);}
.auth-tabs{display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:18px;}
.auth-tab{padding:10px;border-radius:10px;border:1px solid var(--bdr);background:var(--card);cursor:pointer;text-align:center;transition:all .18s;font-family:'DM Sans',sans-serif;font-size:.84rem;font-weight:600;color:var(--t2);}
.auth-tab.active{border-color:var(--gold);background:var(--gf);color:var(--gold);}
.role-tabs{display:grid;grid-template-columns:repeat(3,1fr);gap:6px;margin-bottom:18px;}
.rtab{padding:9px 6px;border-radius:10px;border:1px solid var(--bdr);background:var(--card);cursor:pointer;text-align:center;transition:all .18s;}
.rtab.active{border-color:var(--gold);background:var(--gf);}
.ric{font-size:1.2rem;margin-bottom:3px;}
.rl{font-size:.68rem;font-weight:600;color:var(--t2);}
.rtab.active .rl{color:var(--gold);}
.auth-card{background:var(--card);border:1px solid var(--bdr);border-radius:18px;padding:24px;box-shadow:0 8px 32px rgba(0,0,0,.4);}
.auth-card h3{font-family:'Nunito',sans-serif;font-size:1.05rem;font-weight:800;margin-bottom:16px;color:var(--w);}
.btn-auth{width:100%;padding:12px;background:linear-gradient(135deg,var(--gold),var(--gold2));color:#050e22;border:none;border-radius:10px;font-family:'Nunito',sans-serif;font-size:.94rem;font-weight:800;cursor:pointer;transition:all .18s;margin-top:4px;}
.btn-auth:hover{transform:translateY(-1px);}
.btn-auth:disabled{opacity:.6;cursor:not-allowed;transform:none;}
.err-msg{color:var(--rose);font-size:.75rem;margin-top:8px;text-align:center;}
.success-msg{color:var(--teal);font-size:.75rem;margin-top:8px;text-align:center;}
.enq-link{text-align:center;margin-top:14px;}
.enq-link button{background:none;border:none;color:var(--teal);font-family:'DM Sans',sans-serif;font-size:.8rem;cursor:pointer;text-decoration:underline;}
.pending-box{background:rgba(245,168,0,.07);border:1px solid rgba(245,168,0,.28);border-radius:12px;padding:16px;text-align:center;}
.pending-box .pbi{font-size:2rem;margin-bottom:8px;}
.pending-box h4{font-family:'Nunito',sans-serif;font-size:.95rem;font-weight:800;color:var(--gold);margin-bottom:6px;}
.pending-box p{font-size:.76rem;color:var(--t2);line-height:1.5;}

/* CMS */
.cms-section{background:var(--card);border:1px solid var(--bdr);border-radius:14px;padding:20px;margin-bottom:16px;}
.img-upload-zone{border:2px dashed rgba(245,168,0,.3);border-radius:12px;padding:28px;text-align:center;cursor:pointer;transition:all .18s;margin-bottom:14px;}
.img-upload-zone:hover{border-color:var(--gold);background:var(--gf);}
.approve-btn{padding:4px 10px;border-radius:7px;border:none;background:rgba(34,197,94,.15);color:var(--green);font-family:'DM Sans',sans-serif;font-size:.72rem;font-weight:700;cursor:pointer;}
.reject-btn{padding:4px 10px;border-radius:7px;border:none;background:rgba(244,84,106,.15);color:var(--rose);font-family:'DM Sans',sans-serif;font-size:.72rem;font-weight:700;cursor:pointer;margin-left:6px;}
.pending-row td{background:rgba(245,168,0,.04);}

/* ENQUIRY STATUS */
.enq-new{background:rgba(79,142,247,.13);color:var(--blue);}
.enq-contacted{background:rgba(245,168,0,.13);color:var(--gold);}
.enq-enrolled{background:rgba(34,197,94,.13);color:var(--green);}
.enq-closed{background:rgba(255,255,255,.07);color:var(--t2);}

/* CHILD PORTAL */
.child-hero{background:linear-gradient(135deg,#1a3a70,#0f2550);border:1px solid rgba(245,168,0,.18);border-radius:16px;padding:22px;margin-bottom:16px;position:relative;overflow:hidden;}
.ch-av{width:58px;height:58px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:1.3rem;font-weight:800;margin-bottom:10px;border:3px solid rgba(245,168,0,.35);}
.ch-stats{display:grid;grid-template-columns:repeat(3,1fr);gap:9px;margin-top:13px;}
.chs{background:rgba(255,255,255,.05);border-radius:9px;padding:10px;text-align:center;}
.chs-v{font-family:'Nunito',sans-serif;font-size:1.1rem;font-weight:900;}
.chs-l{font-size:.58rem;color:var(--t2);text-transform:uppercase;letter-spacing:.06em;margin-top:2px;}

/* RESPONSIVE */
@media(max-width:900px){.sg4{grid-template-columns:1fr 1fr;}.gm{grid-template-columns:1fr;}}
@media(max-width:768px){
  .sidebar{transform:translateX(-100%);}
  .sidebar.open{transform:translateX(0);z-index:160;}
  .main{margin-left:0;}
  .bottom-tabs{display:flex;}
  .menu-btn{display:flex;align-items:center;justify-content:center;}
  .content{padding:14px 12px 76px;}
  .topbar{padding:10px 14px;}
  .tb-t h2{font-size:1rem;}
  .tb-t p{display:none;}
  .g2,.gm{grid-template-columns:1fr;}
  .g3,.sg3{grid-template-columns:1fr 1fr;}
  .fr2{grid-template-columns:1fr;}
  .gallery{grid-template-columns:1fr 1fr;}
  .map-frame{height:200px;}
  .modal{padding:18px;border-radius:14px;}
  .ma{flex-direction:column-reverse;}
  .ma .btn{width:100%;justify-content:center;}
  .toast-wrap{bottom:80px;right:8px;}
}
@media(max-width:480px){.sg4{grid-template-columns:1fr 1fr;}.g3{grid-template-columns:1fr;}.gallery{grid-template-columns:1fr;}}
::-webkit-scrollbar{width:5px;height:5px;}
::-webkit-scrollbar-track{background:transparent;}
::-webkit-scrollbar-thumb{background:var(--bdr);border-radius:4px;}
.install-banner{position:fixed;bottom:70px;left:12px;right:12px;background:linear-gradient(135deg,#0f2550,#1a3a70);border:1px solid rgba(245,168,0,.4);border-radius:16px;padding:14px 16px;z-index:400;display:flex;align-items:center;gap:12px;box-shadow:0 8px 32px rgba(0,0,0,.5);animation:slideUp .3s ease;}
@keyframes slideUp{from{transform:translateY(100px);opacity:0}to{transform:translateY(0);opacity:1}}
.install-banner .ib-icon{width:44px;height:44px;border-radius:10px;overflow:hidden;flex-shrink:0;}
.install-banner .ib-icon img{width:100%;height:100%;object-fit:cover;}
.install-banner .ib-text{flex:1;}
.install-banner .ib-title{font-family:'Nunito',sans-serif;font-weight:800;font-size:.85rem;color:var(--w);margin-bottom:2px;}
.install-banner .ib-sub{font-size:.68rem;color:var(--t2);}
.install-banner .ib-btn{background:linear-gradient(135deg,var(--gold),var(--gold2));color:#050e22;border:none;border-radius:9px;padding:7px 14px;font-family:'Nunito',sans-serif;font-weight:800;font-size:.78rem;cursor:pointer;flex-shrink:0;}
.install-banner .ib-close{background:none;border:none;color:var(--t2);font-size:1rem;cursor:pointer;padding:2px 4px;flex-shrink:0;}
`;

/* ══════ SHARED UI ══════ */
function ToastContainer({toasts}){
  return<div className="toast-wrap">{toasts.map(t=><div key={t.id} className={`toast ${t.type||"success"}`}><span>{t.icon||"✓"}</span><span>{t.msg}</span></div>)}</div>;
}
function NoticeCards({notices}){
  return notices.map((n,i)=>{
    const tc=tagColor[n.tag]||"#7b9ccc";
    return(<div key={n.id||i} className="notice-card" style={{background:`linear-gradient(135deg,${tc}12,rgba(15,32,64,.9))`,borderColor:tc+"22"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"4px"}}>
        <span style={{fontSize:".62rem",fontWeight:800,textTransform:"uppercase",letterSpacing:".09em",color:tc}}>{n.tag}</span>
        <span style={{fontSize:".62rem",color:"var(--t2)"}}>{n.date}</span>
      </div>
      <div style={{fontSize:".84rem",color:"var(--t1)"}}>{n.text}</div>
    </div>);
  });
}
function SI({s,showDay}){return(<div className="sched-item" style={{padding:"10px 0"}}>
  <div style={{width:"40px",height:"40px",borderRadius:"10px",background:`${s.color||"#f5a800"}22`,border:`1px solid ${s.color||"#f5a800"}44`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
    <span style={{fontSize:".9rem"}}>{s.name?.includes("Math")?"➗":s.name?.includes("Science")?"🔬":s.name?.includes("English")?"📖":s.name?.includes("All")?"🌟":"📚"}</span>
  </div>
  <div style={{flex:1,marginLeft:"10px"}}>
    <div style={{fontSize:".83rem",fontWeight:700,color:"var(--w)"}}>{s.name||"Class"}</div>
    <div style={{fontSize:".68rem",color:"var(--t2)",marginTop:"2px",display:"flex",alignItems:"center",gap:"6px"}}>
      {showDay&&s.day&&<span style={{color:s.color||"var(--gold)",fontWeight:600}}>{s.day}</span>}
      <span>{s.time}</span>
      {s.meta&&<span>· {s.meta}</span>}
    </div>
  </div>
  {s.room&&<span style={{fontSize:".62rem",padding:"2px 8px",borderRadius:"8px",background:`${s.color||"#4f8ef7"}18`,color:s.color||"var(--blue)",fontWeight:600,border:`1px solid ${s.color||"#4f8ef7"}33`}}>{s.room}</span>}
</div>);}

function Sidebar({user,page,setPage,nav,onLogout,isOpen,onClose,homeData}){
  return(<>
    <div className={`sb-overlay${isOpen?" open":""}`} onClick={onClose}/>
    <div className={`sidebar${isOpen?" open":""}`}>
      <div className="sb-logo"><VLearnLogoH height={48}/><div className="sb-sub">Tharika.P (B.Sc, B.Ed)</div><div className="grow"><span className="gpill">LKG</span><span style={{color:"var(--t3)",fontSize:".6rem"}}>→</span><span className="gpill">10th</span></div></div>
      <div className={`rband ${user.role}`}>
        <span style={{fontSize:"1.05rem"}}>{user.role==="admin"?"🛡️":user.role==="teacher"?"🎓":"👨‍👩‍👧"}</span>
        <div><div className="rbn">{user.name}</div><div className="rbr">{user.role.charAt(0).toUpperCase()+user.role.slice(1)}</div></div>
      </div>
      <div className="sb-nav">{nav.map((sec,i)=>(<div key={i} className="nsec"><div className="nlbl">{sec.label}</div>{sec.items.map(n=>(<div key={n.id} className={`ni${page===n.id?" active":""}`} onClick={()=>{setPage(n.id);onClose();}}>
  <span className="ic" style={{background:n.icBg||"rgba(255,255,255,.08)",color:"#fff"}}>{n.ic}</span>
  <span style={{flex:1}}>{n.label}</span>
  {n.badge>0&&<span className="nbadge">{n.badge}</span>}
</div>))}</div>))}</div>
      <div className="sb-foot"><div className="cbox"><div className="clbl">📞 Contact</div><div className="cph">{homeData?.phone||"9113587199"}</div><div className="cadr">📍 {homeData?.mapAddress||"#578, 11th Cross, Nagharbhavi, Blr-72"}</div></div><button className="lobtn" onClick={onLogout}>🚪 Sign Out</button></div>
    </div>
  </>);
}
function BottomTabs({tabs,page,setPage}){
  return(<div className="bottom-tabs">{tabs.map(t=>(<button key={t.id} className={`btab-item${page===t.id?" active":""}`} onClick={()=>setPage(t.id)}>
  {t.badge>0&&<span className="btab-badge">{t.badge}</span>}
  <span className="btab-icon" style={{background:page===t.id?(t.icBg||"rgba(245,168,0,.2)"):"rgba(255,255,255,.05)"}}>{t.ic}</span>
  <span className="btab-label">{t.label}</span>
</button>))}</div>);
}

/* ══════ AUTH PAGE ══════ */
function AuthPage({onLogin,onEnquiry}){
  const[tab,setTab]=useState("login");
  const[role,setRole]=useState("admin");
  const[email,setEmail]=useState("");
  const[pass,setPass]=useState("");
  const[name,setName]=useState("");
  const[phone,setPhone]=useState("");
  const[err,setErr]=useState("");
  const[success,setSuccess]=useState("");
  const[loading,setLoading]=useState(false);
  const[pendingApproval,setPendingApproval]=useState(false);

  const doLogin=async()=>{
    setErr("");setLoading(true);setPendingApproval(false);
    try{
      const res=await api.login(email,pass);
      localStorage.setItem("vlearn_token",res.token);
      onLogin(res.user);
    }catch(e){
      if(e.message==="PENDING_APPROVAL") setPendingApproval(true);
      else setErr(e.message);
    }finally{setLoading(false);}
  };

  const doRegister=async()=>{
    if(!name||!email||!pass)return setErr("Please fill name, email and password");
    setErr("");setLoading(true);
    try{
      await api.register({name,email,pass,role,phone});
      setSuccess("Registration submitted! Admin will approve your account. You'll be able to login once approved.");
      setTab("login");setEmail(email);setPass("");setName("");setPhone("");
    }catch(e){setErr(e.message);}
    finally{setLoading(false);}
  };

  return(<div className="auth-wrap">
    <div className="auth-box">
      <div className="auth-logo">
        <VLearnLogo size={90} rounded bg/>
        <div style={{display:"flex",justifyContent:"center",marginTop:"10px"}}><div className="gstrip"><span>🎓 Tuition Management System</span></div></div>
      </div>

      <div className="auth-tabs">
        <button className={`auth-tab${tab==="login"?" active":""}`} onClick={()=>{setTab("login");setErr("");setPendingApproval(false);}}>Sign In</button>
        <button className={`auth-tab${tab==="register"?" active":""}`} onClick={()=>{setTab("register");setErr("");setPendingApproval(false);}}>Register</button>
      </div>

      {tab==="forgot"&&(<div className="auth-card">
        <div className="mh"><h3>🔑 Reset Password</h3><button className="cls" onClick={()=>setTab("login")}>✕</button></div>
        <div style={{fontSize:".8rem",color:"var(--t2)",marginBottom:"16px",background:"rgba(79,142,247,.08)",border:"1px solid rgba(79,142,247,.18)",borderRadius:"9px",padding:"10px 12px"}}>
          Enter your registered email. Contact admin at <strong style={{color:"var(--gold)"}}>9113587199</strong> with your email and they will reset your password.
        </div>
        <div className="fg"><label className="fl">Registered Email</label><input className="fi" type="email" placeholder="your@email.com" value={email} onChange={e=>setEmail(e.target.value)}/></div>
        <div style={{display:"flex",gap:"10px",marginTop:"4px"}}>
          <a href={`https://wa.me/919113587199?text=${encodeURIComponent("Hello, I forgot my password. My registered email is: "+email)}`} target="_blank" rel="noreferrer" className="btn-auth" style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:"6px",textDecoration:"none",background:"linear-gradient(135deg,#25d366,#128c7e)",color:"#fff",borderRadius:"10px",padding:"12px",fontFamily:"'Nunito',sans-serif",fontWeight:800,fontSize:".88rem"}}>💬 WhatsApp Admin</a>
        </div>
        <button style={{width:"100%",marginTop:"10px",padding:"10px",background:"none",border:"1px solid var(--bdr)",borderRadius:"10px",color:"var(--t2)",fontFamily:"'DM Sans',sans-serif",fontSize:".8rem",cursor:"pointer"}} onClick={()=>setTab("login")}>← Back to Sign In</button>
      </div>)}

      {tab==="login"&&(<div className="auth-card">
        <h3>👋 Welcome back</h3>
        <div className="role-tabs">
          {[{id:"admin",ic:"🛡️",lb:"Admin"},{id:"teacher",ic:"🎓",lb:"Teacher"},{id:"parent",ic:"👨‍👩‍👧",lb:"Parent"}].map(r=>(
            <div key={r.id} className={`rtab${role===r.id?" active":""}`} onClick={()=>{setRole(r.id);setEmail("");setPass("");setErr("");setPendingApproval(false);}}>
              <div className="ric">{r.ic}</div><div className="rl">{r.lb}</div>
            </div>
          ))}
        </div>
        {pendingApproval?(<div className="pending-box">
          <div className="pbi">⏳</div>
          <h4>Awaiting Admin Approval</h4>
          <p>Your account has been registered but is pending approval from the admin. Once approved, you can sign in with your credentials.</p>
          <p style={{marginTop:"8px",color:"var(--gold)",fontSize:".72rem"}}>Contact: 9113587199</p>
        </div>):(<>
          <div className="fg"><label className="fl">Email / Username</label><input className="fi" type="email" placeholder="Your email address" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&doLogin()}/></div>
          <div className="fg"><label className="fl">Password</label><input className="fi" type="password" placeholder="Your password" value={pass} onChange={e=>setPass(e.target.value)} onKeyDown={e=>e.key==="Enter"&&doLogin()}/></div>
          {success&&<div className="success-msg">✓ {success}</div>}
          {err&&<div className="err-msg">⚠ {err}</div>}
          <button className="btn-auth" onClick={doLogin} disabled={loading}>{loading?"Signing in…":"Sign In →"}</button>
        </>)}
      </div>)}

      {tab==="register"&&(<div className="auth-card">
        <h3>📝 Create Account</h3>
        <div style={{fontSize:".76rem",color:"var(--t2)",background:"rgba(79,142,247,.08)",border:"1px solid rgba(79,142,247,.18)",borderRadius:"9px",padding:"10px 12px",marginBottom:"14px"}}>
          ℹ️ Registration requires <strong style={{color:"var(--blue)"}}>admin approval</strong> before you can login. For parents, admin will link your account to your child.
        </div>
        <div className="role-tabs">
          {[{id:"teacher",ic:"🎓",lb:"Teacher"},{id:"parent",ic:"👨‍👩‍👧",lb:"Parent"}].map(r=>(
            <div key={r.id} className={`rtab${role===r.id?" active":""}`} onClick={()=>setRole(r.id)}>
              <div className="ric">{r.ic}</div><div className="rl">{r.lb}</div>
            </div>
          ))}
        </div>
        <div className="fg"><label className="fl">Full Name *</label><input className="fi" placeholder="Your full name" value={name} onChange={e=>setName(e.target.value)}/></div>
        <div className="fr2">
          <div className="fg"><label className="fl">Email *</label><input className="fi" type="email" placeholder="Email address" value={email} onChange={e=>setEmail(e.target.value)}/></div>
          <div className="fg"><label className="fl">Phone</label><input className="fi" placeholder="Phone number" value={phone} onChange={e=>setPhone(e.target.value)}/></div>
        </div>
        <div className="fg"><label className="fl">Password *</label><input className="fi" type="password" placeholder="Choose a password" value={pass} onChange={e=>setPass(e.target.value)}/></div>
        {err&&<div className="err-msg">⚠ {err}</div>}
        <button className="btn-auth" onClick={doRegister} disabled={loading}>{loading?"Registering…":"Register →"}</button>
      </div>)}

      <div className="enq-link"><button onClick={onEnquiry}>📋 Student Enquiry & Fee Preview</button></div>
    </div>
  </div>);
}

/* ══════ ENQUIRY PAGE (public) ══════ */
function EnquiryPage({onBack,feeStruct}){
  const[f,setF]=useState({name:"",phone:"",email:"",grade:"",subject:"",message:""});
  const up=(k,v)=>setF(p=>({...p,[k]:v}));
  const[sent,setSent]=useState(false);
  const[loading,setLoading]=useState(false);
  const[err,setErr]=useState("");
  const avSubs=ALL_SUBJECTS;
  const liveFee=f.grade&&f.subject?feeStruct[f.grade]?.[f.subject]:null;

  const submit=async()=>{
    if(!f.name||!f.phone)return setErr("Name and phone number are required");
    setLoading(true);setErr("");
    try{
      await api.submitEnquiry(f);
      setSent(true);
    }catch(e){setErr(e.message);}
    finally{setLoading(false);}
  };

  return(<div className="auth-wrap">
    <div className="auth-box" style={{maxWidth:"560px"}}>
      <div className="auth-logo"><VLearnLogo size={70} rounded bg/></div>
      {sent?(<div className="auth-card" style={{textAlign:"center"}}>
        <div style={{fontSize:"3rem",marginBottom:"12px"}}>✅</div>
        <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:800,fontSize:"1.1rem",color:"var(--gold)",marginBottom:"8px"}}>Enquiry Submitted!</div>
        <p style={{fontSize:".82rem",color:"var(--t2)",marginBottom:"16px"}}>We will contact you at <strong style={{color:"var(--w)"}}>{f.phone}</strong> shortly. Thank you for your interest in V Learn!</p>
        <button className="btn-auth" onClick={onBack}>← Back to Login</button>
      </div>):(<div className="auth-card">
        <div className="mh"><h3>📋 Student Enquiry</h3><button className="cls" onClick={onBack}>✕</button></div>
        <div className="fr2" style={{marginBottom:"12px"}}>
          <div className="fg"><label className="fl">Your Name *</label><input className="fi" placeholder="Parent / Guardian name" value={f.name} onChange={e=>up("name",e.target.value)}/></div>
          <div className="fg"><label className="fl">Phone *</label><input className="fi" placeholder="Mobile number" value={f.phone} onChange={e=>up("phone",e.target.value)}/></div>
        </div>
        <div className="fg"><label className="fl">Email</label><input className="fi" type="email" placeholder="Email (optional)" value={f.email} onChange={e=>up("email",e.target.value)}/></div>
        <div className="fr2" style={{marginBottom:"12px"}}>
          <div className="fg"><label className="fl">Child's Class</label><select className="fi" value={f.grade} onChange={e=>{up("grade",e.target.value);up("subject","");}}><option value="">— Select Class —</option>{GRADES.map(g=><option key={g}>{g}</option>)}</select></div>
          <div className="fg"><label className="fl">Subject</label><select className="fi" value={f.subject} onChange={e=>up("subject",e.target.value)}><option value="">— Select Subject —</option>{avSubs.map(s=><option key={s}>{s}</option>)}</select></div>
        </div>
        {liveFee&&<div style={{background:"var(--gf)",border:"2px solid rgba(245,168,0,.4)",borderRadius:"12px",padding:"14px",textAlign:"center",marginBottom:"12px"}}><div style={{fontSize:".72rem",color:"var(--t2)",marginBottom:"4px"}}>Monthly Fee — {f.grade} · {f.subject}</div><div style={{fontFamily:"'Nunito',sans-serif",fontWeight:900,color:"var(--gold)",fontSize:"1.8rem"}}>₹{liveFee.toLocaleString()}/mo</div></div>}
        <div className="fg"><label className="fl">Message (optional)</label><textarea className="fi" rows={3} placeholder="Any specific requirements..." value={f.message} onChange={e=>up("message",e.target.value)}/></div>
        {err&&<div className="err-msg">⚠ {err}</div>}
        <button className="btn-auth" onClick={submit} disabled={loading}>{loading?"Submitting…":"Submit Enquiry ✓"}</button>
        <div style={{textAlign:"center",marginTop:"12px",fontSize:".76rem",color:"var(--t2)"}}>📞 Or call directly: <strong style={{color:"var(--gold)"}}>9113587199</strong></div>
      </div>)}
    </div>
  </div>);
}

/* ══════ PARENT APP ══════ */


/* ══════ ADMIN ATTENDANCE PAGE ══════ */
function AdminAttendancePage({students,addToast}){
  const[selected,setSelected]=useState(null);
  const[filterMonth,setFilterMonth]=useState("all");

  const parseMonth=(dateStr)=>{
    if(!dateStr)return "Unknown";
    const parts=dateStr.includes("-")?dateStr.split("-"):dateStr.split(" ");
    if(parts.length>=3)return `${parts[1]} ${parts[2]}`;
    return "Unknown";
  };
  const parseDay=(dateStr)=>{
    if(!dateStr)return 0;
    const parts=dateStr.includes("-")?dateStr.split("-"):dateStr.split(" ");
    return parseInt(parts[0])||0;
  };

  // Get all unique months across all students
  const allMonths=new Set();
  students.forEach(s=>(s.attendance||[]).forEach(a=>allMonths.add(parseMonth(a.date))));
  const months=[...allMonths].sort((a,b)=>b.localeCompare(a));

  return(<div className="fu">
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"14px",flexWrap:"wrap",gap:"10px"}}>
      <div>
        <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:900,fontSize:"1rem",color:"var(--w)"}}>📅 Attendance Records</div>
        <div style={{fontSize:".72rem",color:"var(--t2)",marginTop:"2px"}}>All students · full history</div>
      </div>
    </div>

    {/* Summary cards */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"10px",marginBottom:"14px"}}>
      {[
        {lbl:"Students",val:students.length,bg:"rgba(79,142,247,.1)",clr:"var(--blue)"},
        {lbl:"Avg Attendance",val:`${Math.round(students.reduce((a,s)=>a+s.att,0)/(students.length||1))}%`,bg:"rgba(34,211,200,.1)",clr:"var(--teal)"},
        {lbl:"Total Records",val:students.reduce((a,s)=>a+(s.attendance?.length||0),0),bg:"rgba(245,168,0,.1)",clr:"var(--gold)"},
      ].map((c,i)=><div key={i} style={{background:c.bg,borderRadius:"12px",padding:"12px",textAlign:"center"}}>
        <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:900,fontSize:"1.3rem",color:c.clr}}>{c.val}</div>
        <div style={{fontSize:".62rem",color:"var(--t2)",marginTop:"2px",textTransform:"uppercase",fontWeight:600}}>{c.lbl}</div>
      </div>)}
    </div>

    {/* Month filter */}
    <select className="fi" style={{marginBottom:"14px"}} value={filterMonth} onChange={e=>setFilterMonth(e.target.value)}>
      <option value="all">All Months</option>
      {months.map(m=><option key={m} value={m}>{m}</option>)}
    </select>

    {/* Per-student expandable rows */}
    {students.map((s,si)=>{
      const att=filterMonth==="all"?s.attendance||[]:( s.attendance||[]).filter(a=>parseMonth(a.date)===filterMonth);
      const present=att.filter(a=>a.status==="present").length;
      const absent=att.filter(a=>a.status==="absent").length;
      const pct=att.length?Math.round(present/att.length*100):s.att;
      const isOpen=selected===s.id;
      return(<div key={s.id} style={{background:"var(--card)",border:"1px solid var(--bdr)",borderRadius:"14px",marginBottom:"8px",overflow:"hidden"}}>
        {/* Student row */}
        <div style={{padding:"12px 16px",display:"flex",alignItems:"center",gap:"12px",cursor:"pointer"}} onClick={()=>setSelected(isOpen?null:s.id)}>
          <div className={`av ${getAv(si)}`}>{ini(s.name)}</div>
          <div style={{flex:1}}>
            <div style={{fontWeight:700,fontSize:".86rem",color:"var(--w)"}}>{s.name}</div>
            <div style={{fontSize:".68rem",color:"var(--t2)"}}>{s.grade} · {att.length} records {filterMonth!=="all"?`in ${filterMonth}`:""}</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
            <span style={{fontSize:".7rem",color:"var(--teal)",fontWeight:600}}>✓{present}</span>
            <span style={{fontSize:".7rem",color:"var(--rose)",fontWeight:600}}>✗{absent}</span>
            <span style={{fontFamily:"'Nunito',sans-serif",fontWeight:900,fontSize:".95rem",color:pct>=85?"var(--teal)":pct>=75?"var(--gold)":"var(--rose)"}}>{pct}%</span>
          </div>
          <span style={{color:"var(--t2)",fontSize:".75rem",transition:"transform .2s",transform:isOpen?"rotate(180deg)":"rotate(0deg)"}}>▼</span>
        </div>
        {/* Progress bar */}
        <div style={{height:"3px",background:"rgba(255,255,255,.05)"}}>
          <div style={{height:"100%",width:`${pct}%`,background:pct>=85?"var(--teal)":pct>=75?"var(--gold)":"var(--rose)"}}/>
        </div>

        {/* Expanded - grouped by month with list */}
        {isOpen&&(<div style={{borderTop:"1px solid var(--bdr)",background:"rgba(255,255,255,.02)"}}>
          {att.length===0
            ?<div style={{padding:"20px",textAlign:"center",color:"var(--t2)",fontSize:".8rem"}}>
                <div style={{fontSize:"1.5rem",marginBottom:"6px"}}>📅</div>
                No attendance records {filterMonth!=="all"?`for ${filterMonth}`:"yet"}
              </div>
            :<>
              {/* Mini summary inside expanded */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"6px",padding:"10px 12px",borderBottom:"1px solid var(--bdr)"}}>
                <div style={{background:"rgba(34,211,200,.1)",borderRadius:"8px",padding:"8px",textAlign:"center"}}>
                  <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:800,fontSize:"1.1rem",color:"var(--teal)"}}>{present}</div>
                  <div style={{fontSize:".6rem",color:"var(--t2)"}}>PRESENT</div>
                </div>
                <div style={{background:"rgba(244,84,106,.1)",borderRadius:"8px",padding:"8px",textAlign:"center"}}>
                  <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:800,fontSize:"1.1rem",color:"var(--rose)"}}>{absent}</div>
                  <div style={{fontSize:".6rem",color:"var(--t2)"}}>ABSENT</div>
                </div>
                <div style={{background:"rgba(245,168,0,.1)",borderRadius:"8px",padding:"8px",textAlign:"center"}}>
                  <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:800,fontSize:"1.1rem",color:"var(--gold)"}}>{att.filter(a=>a.status==="late").length}</div>
                  <div style={{fontSize:".6rem",color:"var(--t2)"}}>LATE</div>
                </div>
              </div>
              {/* Records list - newest first, max height with scroll */}
              <div style={{maxHeight:"300px",overflowY:"auto"}}>
                {[...att].sort((a,b)=>b.date?.localeCompare(a.date)||0).map((a,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:"10px",padding:"9px 14px",borderBottom:i<att.length-1?"1px solid rgba(255,255,255,.04)":"none"}}>
                    <div style={{width:"32px",height:"32px",borderRadius:"8px",flexShrink:0,
                      background:a.status==="present"?"rgba(34,211,200,.18)":a.status==="absent"?"rgba(244,84,106,.18)":"rgba(245,168,0,.18)",
                      display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1rem",
                      border:`1px solid ${a.status==="present"?"rgba(34,211,200,.3)":a.status==="absent"?"rgba(244,84,106,.3)":"rgba(245,168,0,.3)"}`}}>
                      {a.status==="present"?"✓":a.status==="absent"?"✗":"↷"}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:".82rem",fontWeight:600,color:"var(--w)"}}>{a.date}</div>
                      {a.marked_by&&<div style={{fontSize:".62rem",color:"var(--t2)"}}>by {a.marked_by}</div>}
                    </div>
                    <span className={`bd ${a.status==="present"?"bg3":a.status==="absent"?"br3":"bo"}`} style={{textTransform:"capitalize",fontSize:".65rem"}}>{a.status}</span>
                  </div>
                ))}
              </div>
            </>
          }
        </div>)}
      </div>);
    })}
  </div>);
}

/* ══════ ATTENDANCE VIEW (Parent) ══════ */
function AttendanceView({child}){
  const[viewMode,setViewMode]=useState("calendar"); // "calendar" or "list"
  const allAtt=child.attendance||[];
  const totalDays=allAtt.length;
  const presentDays=allAtt.filter(a=>a.status==="present").length;
  const absentDays=allAtt.filter(a=>a.status==="absent").length;
  const lateDays=allAtt.filter(a=>a.status==="late").length;

  const parseMonth=(dateStr)=>{
    if(!dateStr)return "Unknown";
    const parts=dateStr.includes("-")?dateStr.split("-"):dateStr.split(" ");
    if(parts.length>=3) return `${parts[1]} ${parts[2]}`;
    return dateStr.slice(0,7)||"Unknown";
  };
  const parseDay=(dateStr)=>{
    if(!dateStr)return 0;
    const parts=dateStr.includes("-")?dateStr.split("-"):dateStr.split(" ");
    return parseInt(parts[0])||0;
  };

  const byMonth={};
  allAtt.forEach(a=>{
    const mk=parseMonth(a.date);
    if(!byMonth[mk])byMonth[mk]=[];
    byMonth[mk].push(a);
  });
  const sortedMonths=Object.entries(byMonth).sort((a,b)=>b[0].localeCompare(a[0]));
  const attClr2=p=>p>=85?"var(--teal)":p>=75?"var(--gold)":"var(--rose)";
  return(<div className="fu">
    {/* Stats */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"10px",marginBottom:"14px"}}>
      <div style={{background:"rgba(34,211,200,.1)",border:"1px solid rgba(34,211,200,.2)",borderRadius:"12px",padding:"14px",textAlign:"center"}}>
        <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:900,fontSize:"1.6rem",color:"var(--teal)"}}>{presentDays}</div>
        <div style={{fontSize:".65rem",color:"var(--t2)",marginTop:"3px",textTransform:"uppercase",fontWeight:600}}>Present</div>
      </div>
      <div style={{background:"rgba(244,84,106,.1)",border:"1px solid rgba(244,84,106,.2)",borderRadius:"12px",padding:"14px",textAlign:"center"}}>
        <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:900,fontSize:"1.6rem",color:"var(--rose)"}}>{absentDays}</div>
        <div style={{fontSize:".65rem",color:"var(--t2)",marginTop:"3px",textTransform:"uppercase",fontWeight:600}}>Absent</div>
      </div>
      <div style={{background:"rgba(245,168,0,.1)",border:"1px solid rgba(245,168,0,.2)",borderRadius:"12px",padding:"14px",textAlign:"center"}}>
        <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:900,fontSize:"1.6rem",color:"var(--gold)"}}>{child.att}%</div>
        <div style={{fontSize:".65rem",color:"var(--t2)",marginTop:"3px",textTransform:"uppercase",fontWeight:600}}>Overall</div>
      </div>
    </div>

    {/* View Toggle */}
    <div style={{display:"flex",background:"rgba(255,255,255,.04)",borderRadius:"10px",padding:"3px",marginBottom:"14px"}}>
      <button onClick={()=>setViewMode("calendar")} style={{flex:1,padding:"7px",borderRadius:"8px",border:"none",fontFamily:"'DM Sans',sans-serif",fontSize:".78rem",fontWeight:600,cursor:"pointer",background:viewMode==="calendar"?"var(--card)":"transparent",color:viewMode==="calendar"?"var(--gold)":"var(--t2)",transition:"all .18s"}}>📅 Calendar</button>
      <button onClick={()=>setViewMode("list")} style={{flex:1,padding:"7px",borderRadius:"8px",border:"none",fontFamily:"'DM Sans',sans-serif",fontSize:".78rem",fontWeight:600,cursor:"pointer",background:viewMode==="list"?"var(--card)":"transparent",color:viewMode==="list"?"var(--gold)":"var(--t2)",transition:"all .18s"}}>📋 Full History</button>
    </div>
    {/* Progress bar always visible */}
    <div style={{background:"var(--card)",border:"1px solid var(--bdr)",borderRadius:"12px",padding:"14px",marginBottom:"14px"}}>
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:"8px"}}>
        <span style={{fontSize:".78rem",fontWeight:600,color:"var(--w)"}}>Attendance Rate</span>
        <span style={{fontSize:".78rem",fontWeight:700,color:attClr2(child.att)}}>{child.att}% {child.att>=85?"✓ Excellent":child.att>=75?"Good":"Needs Improvement"}</span>
      </div>
      <div style={{background:"rgba(255,255,255,.07)",borderRadius:"8px",height:"10px",overflow:"hidden"}}>
        <div style={{height:"100%",width:`${child.att}%`,background:child.att>=85?"linear-gradient(90deg,#22d3c8,#5ef5ee)":child.att>=75?"linear-gradient(90deg,#f5a800,#ffd050)":"linear-gradient(90deg,#f4546a,#f89ba5)",borderRadius:"8px"}}/>
      </div>
      <div style={{display:"flex",justifyContent:"space-between",marginTop:"6px",fontSize:".65rem",color:"var(--t2)"}}>
        <span>{totalDays} days recorded</span>
        {lateDays>0&&<span style={{color:"var(--gold)"}}>{lateDays} late</span>}
      </div>
    </div>

    {totalDays===0&&<div style={{textAlign:"center",padding:"40px",color:"var(--t2)",fontSize:".85rem"}}><div style={{fontSize:"2rem",marginBottom:"8px"}}>📅</div>No attendance records yet</div>}

    {/* CALENDAR VIEW */}
    {viewMode==="calendar"&&totalDays>0&&sortedMonths.map(([month,records])=>{
      // Build a day lookup for this month
      const dayMap={};
      records.forEach(r=>{const d=parseDay(r.date);if(d)dayMap[d]=r.status;});
      const monthParts=month.split(" ");
      const MONTH_NAMES=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      const monthIdx=MONTH_NAMES.indexOf(monthParts[0]);
      const year=parseInt(monthParts[1])||2026;
      const daysInMonth=monthIdx>=0?new Date(year,monthIdx+1,0).getDate():31;
      const presentM=records.filter(r=>r.status==="present").length;
      const absentM=records.filter(r=>r.status==="absent").length;
      return(<div key={month} style={{background:"var(--card)",border:"1px solid var(--bdr)",borderRadius:"14px",marginBottom:"14px",overflow:"hidden"}}>
        <div style={{padding:"12px 16px",borderBottom:"1px solid var(--bdr)",display:"flex",justifyContent:"space-between",alignItems:"center",background:"rgba(255,255,255,.03)"}}>
          <span style={{fontFamily:"'Nunito',sans-serif",fontWeight:800,fontSize:".9rem",color:"var(--w)"}}>{month}</span>
          <div style={{display:"flex",gap:"10px"}}>
            <span style={{fontSize:".72rem",color:"var(--teal)",fontWeight:700}}>✓ {presentM} Present</span>
            <span style={{fontSize:".72rem",color:"var(--rose)",fontWeight:700}}>✗ {absentM} Absent</span>
          </div>
        </div>
        <div style={{padding:"12px"}}>
          {/* Day headers */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:"3px",marginBottom:"4px"}}>
            {["S","M","T","W","T","F","S"].map((d,i)=><div key={i} style={{textAlign:"center",fontSize:".6rem",color:"var(--t2)",fontWeight:600,padding:"2px 0"}}>{d}</div>)}
          </div>
          {/* Day cells */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:"3px"}}>
            {Array.from({length:daysInMonth}).map((_,i)=>{
              const day=i+1;
              const status=dayMap[day];
              const bg=status==="present"?"rgba(34,211,200,.2)":status==="absent"?"rgba(244,84,106,.2)":status==="late"?"rgba(245,168,0,.2)":"rgba(255,255,255,.04)";
              const clr=status==="present"?"var(--teal)":status==="absent"?"var(--rose)":status==="late"?"var(--gold)":"var(--t3)";
              const border=status?"1px solid "+clr.replace("var(","").replace(")",""):undefined;
              return(<div key={day} style={{aspectRatio:"1",borderRadius:"6px",background:bg,border,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontSize:".65rem",fontWeight:status?700:400,color:clr}}>
                <span>{day}</span>
                {status==="present"&&<span style={{fontSize:".5rem"}}>✓</span>}
                {status==="absent"&&<span style={{fontSize:".5rem"}}>✗</span>}
                {status==="late"&&<span style={{fontSize:".5rem"}}>↷</span>}
              </div>);
            })}
          </div>
          {/* Legend */}
          <div style={{display:"flex",gap:"12px",marginTop:"10px",justifyContent:"center"}}>
            {[["var(--teal)","rgba(34,211,200,.2)","Present"],["var(--rose)","rgba(244,84,106,.2)","Absent"],["var(--gold)","rgba(245,168,0,.2)","Late"]].map(([clr,bg,lbl])=>(
              <div key={lbl} style={{display:"flex",alignItems:"center",gap:"4px"}}>
                <div style={{width:"12px",height:"12px",borderRadius:"3px",background:bg,border:`1px solid ${clr}`}}/>
                <span style={{fontSize:".6rem",color:"var(--t2)"}}>{lbl}</span>
              </div>
            ))}
          </div>
        </div>
      </div>);
    })}

    {/* LIST VIEW - full history */}
    {viewMode==="list"&&totalDays>0&&sortedMonths.map(([month,records])=>(
      <div key={month} style={{background:"var(--card)",border:"1px solid var(--bdr)",borderRadius:"14px",marginBottom:"12px",overflow:"hidden"}}>
        <div style={{padding:"10px 16px",borderBottom:"1px solid var(--bdr)",display:"flex",justifyContent:"space-between",alignItems:"center",background:"rgba(255,255,255,.03)"}}>
          <span style={{fontWeight:700,fontSize:".85rem",color:"var(--w)"}}>{month}</span>
          <div style={{display:"flex",gap:"8px"}}>
            <span style={{fontSize:".7rem",color:"var(--teal)",fontWeight:600}}>✓ {records.filter(r=>r.status==="present").length}P</span>
            <span style={{fontSize:".7rem",color:"var(--rose)",fontWeight:600}}>✗ {records.filter(r=>r.status==="absent").length}A</span>
          </div>
        </div>
        <div style={{padding:"4px 8px"}}>
          {records.sort((a,b)=>parseDay(b.date)-parseDay(a.date)).map((a,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px",borderBottom:i<records.length-1?"1px solid rgba(255,255,255,.04)":"none"}}>
              <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
                <div style={{width:"32px",height:"32px",borderRadius:"8px",background:a.status==="present"?"rgba(34,211,200,.15)":a.status==="absent"?"rgba(244,84,106,.15)":"rgba(245,168,0,.15)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1rem"}}>
                  {a.status==="present"?"✓":a.status==="absent"?"✗":"↷"}
                </div>
                <span style={{fontSize:".82rem",fontWeight:600,color:"var(--w)"}}>{a.date}</span>
              </div>
              <span className={`bd ${a.status==="present"?"bg3":a.status==="absent"?"br3":"bo"}`} style={{textTransform:"capitalize"}}>{a.status}</span>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>);
}

function ParentApp({user,feeStruct,homeData,notices,students,onLogout}){
  // Find child: try by JWT childId first, then by student.parent_id matching user.id
  const child=students.find(s=>s.id===user.childId) || students.find(s=>s.parent_id===user.id);
  const[page,setPage]=useState("home");
  const[sbOpen,setSbOpen]=useState(false);
  const nav=[
    {label:"My Child",items:[{id:"home",ic:"🏠",label:"Overview",icBg:"linear-gradient(135deg,#f5a800,#ffd050)"},{id:"marks",ic:"📊",label:"Marks",icBg:"linear-gradient(135deg,#4f8ef7,#82b0ff)"},{id:"attendance",ic:"📅",label:"Attendance",icBg:"linear-gradient(135deg,#22d3c8,#5ef5ee)"},{id:"fees",ic:"💰",label:"Fee Status",icBg:"linear-gradient(135deg,#22c55e,#4ade80)"}]},
    {label:"Centre",items:[{id:"notices",ic:"📢",label:"Notices",icBg:"linear-gradient(135deg,#f4546a,#f89ba5)"},{id:"schedule",ic:"🗓️",label:"Schedule",icBg:"linear-gradient(135deg,#06b6d4,#38bdf8)"},{id:"gallery_p",ic:"📸",label:"Gallery",icBg:"linear-gradient(135deg,#a78bfa,#c4b5fd)"},{id:"map_p",ic:"📍",label:"Location",icBg:"linear-gradient(135deg,#f4546a,#f89ba5)"}]},
  ];
  const btabs=[{id:"home",ic:"🏠",label:"Home",icBg:"linear-gradient(135deg,#f5a800,#ffd050)"},{id:"marks",ic:"📊",label:"Marks",icBg:"linear-gradient(135deg,#4f8ef7,#82b0ff)"},{id:"attendance",ic:"📅",label:"Attend.",icBg:"linear-gradient(135deg,#22d3c8,#5ef5ee)"},{id:"notices",ic:"📢",label:"Notices",icBg:"linear-gradient(135deg,#f4546a,#f89ba5)"},{id:"gallery_p",ic:"📸",label:"Gallery",icBg:"linear-gradient(135deg,#a78bfa,#c4b5fd)"}];
  const ptitles={home:"My Child",marks:"Marks & Tests",attendance:"Attendance",fees:"Fee Status",notices:"Notices",schedule:"Schedule",gallery_p:"Gallery",map_p:"Location"};

  if(!child)return(<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",flexDirection:"column",gap:16}}>
    <div style={{fontSize:"2rem"}}>😕</div>
    <div style={{color:"var(--t2)",fontSize:".85rem",textAlign:"center",maxWidth:"260px"}}>Your account is not linked to a student yet. Please contact the admin.</div>
    <div style={{fontSize:".76rem",color:"var(--gold)"}}>📞 9113587199</div>
    <button className="btn bg2" onClick={onLogout}>Sign Out</button>
  </div>);

  const lastTest=child.tests?.length?child.tests[child.tests.length-1]:null;
  const fee=feeStruct[child.grade]?.[child.subject];

  return(<div className="shell">
    <style>{CSS}</style>
    <Sidebar user={user} page={page} setPage={setPage} nav={nav} onLogout={onLogout} isOpen={sbOpen} onClose={()=>setSbOpen(false)} homeData={homeData}/>
    <div className="main">
      <div className="topbar">
        <button className="menu-btn" onClick={()=>setSbOpen(!sbOpen)}>☰</button>
        <div className="tb-t"><h2>{ptitles[page]}</h2><p><span className="live-dot"></span>Live</p></div>
        <div className="tb-a"><div style={{fontSize:".76rem",color:"var(--t2)"}}>{todayStr()}</div></div>
      </div>
      <div className="content">

        {page==="home"&&(<>
          {/* Child Stats Card */}
          <div className="child-hero fu" style={{marginBottom:"14px"}}>
            <div style={{display:"flex",alignItems:"center",gap:"14px",marginBottom:"12px"}}>
              <div className="ch-av av1" style={{width:"52px",height:"52px",fontSize:"1.1rem",fontWeight:800}}>{ini(child.name)}</div>
              <div><div style={{fontFamily:"'Nunito',sans-serif",fontSize:"1.1rem",fontWeight:900,color:"var(--w)"}}>{child.name}</div><div style={{fontSize:".74rem",color:"var(--t2)",marginTop:"1px"}}>{child.grade} · {child.subject}</div></div>
            </div>
            <div className="ch-stats">
              <div className="chs"><div className="chs-v" style={{color:attClr(child.att)}}>{child.att}%</div><div className="chs-l">Attendance</div></div>
              <div className="chs"><div className="chs-v" style={{color:lastTest?(lastTest.marks/lastTest.total>=0.8?"var(--teal)":lastTest.marks/lastTest.total>=0.6?"var(--gold)":"var(--rose)"):"var(--t2)"}}>{lastTest?`${lastTest.marks}/${lastTest.total}`:"—"}</div><div className="chs-l">Last Score</div></div>
              <div className="chs"><div className={`chs-v ${child.feeStatus==="Paid"?"up":"dn"}`}>{child.feeStatus}</div><div className="chs-l">Fee</div></div>
            </div>
          </div>

          {/* Centre Hero Banner - from admin CMS */}
          <div style={{background:"linear-gradient(135deg,#0f2550,#1a3a70)",border:"1px solid rgba(245,168,0,.25)",borderRadius:"14px",padding:"18px",marginBottom:"14px"}} className="fu2">
            <div style={{fontFamily:"'Nunito',sans-serif",fontSize:"1.05rem",fontWeight:900,color:"var(--w)",marginBottom:"3px"}}>{homeData.heroTitle||"Building Strong Foundations"}</div>
            <div style={{fontSize:".8rem",color:"var(--gold)",fontWeight:700,marginBottom:"8px"}}>{homeData.heroSubtitle||"for Bright Futures 🌟"}</div>
            <div style={{fontSize:".76rem",color:"var(--t2)",lineHeight:1.5,marginBottom:"12px"}}>{homeData.heroDesc||"Quality tuition for LKG to 10th Std"}</div>
            <div style={{display:"flex",alignItems:"center",gap:"14px",flexWrap:"wrap",marginBottom:"12px"}}>
              <a href={`tel:${homeData.phone||"9113587199"}`} style={{display:"flex",alignItems:"center",gap:"6px",color:"var(--gold)",fontWeight:800,fontSize:".83rem",textDecoration:"none",background:"rgba(245,168,0,.1)",border:"1px solid rgba(245,168,0,.25)",padding:"5px 12px",borderRadius:"8px"}}>📞 {homeData.phone||"9113587199"}</a>
              <span style={{fontSize:".72rem",color:"var(--t2)",display:"flex",alignItems:"center",gap:"4px"}}>🕐 {homeData.timings||"Mon–Sat 8AM–5PM"}</span>
            </div>
            {Array.isArray(homeData.features)&&homeData.features.length>0&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"7px"}}>
              {homeData.features.slice(0,4).map((f,i)=>(<div key={i} style={{background:"rgba(255,255,255,.05)",borderRadius:"9px",padding:"8px 10px",display:"flex",alignItems:"center",gap:"7px"}}>
                <span style={{fontSize:"1rem"}}>{f.icon||"📚"}</span>
                <div><div style={{fontSize:".72rem",fontWeight:700,color:"var(--w)"}}>{f.title||""}</div><div style={{fontSize:".6rem",color:"var(--t2)"}}>{f.desc||""}</div></div>
              </div>))}
            </div>}
            <div style={{display:"flex",gap:"8px",marginTop:"12px"}}>
              <button className="btn bg2" style={{fontSize:".73rem",padding:"5px 12px",flex:1}} onClick={()=>setPage("gallery_p")}>📸 Gallery</button>
              <button className="btn bg2" style={{fontSize:".73rem",padding:"5px 12px",flex:1}} onClick={()=>setPage("map_p")}>📍 Location</button>
              <button className="btn bg2" style={{fontSize:".73rem",padding:"5px 12px",flex:1}} onClick={()=>setPage("schedule")}>🗓️ Schedule</button>
            </div>
          </div>

          {/* Notices & Schedule */}
          <div className="g2">
            <div className="card fu3"><div className="ch"><span className="ct">📢 Latest Notices</span></div><div className="cb" style={{padding:"8px 14px"}}><NoticeCards notices={notices.slice(0,3)}/></div></div>
            <div className="card fu3"><div className="ch"><span className="ct">🗓️ Today's Classes</span></div><div className="cb" style={{padding:"6px 18px"}}>{(homeData.schedule?.length?homeData.schedule:[]).slice(0,4).map((s,i)=><SI key={i} s={s}/>)}{!homeData.schedule?.length&&<div style={{color:"var(--t2)",fontSize:".8rem",padding:"10px 0"}}>No schedule set yet</div>}</div></div>
          </div>

          {/* Contact Section */}
          <div style={{background:"linear-gradient(135deg,#071428,#0c1d3e)",border:"1px solid rgba(255,255,255,.08)",borderRadius:"16px",padding:"20px",marginBottom:"14px"}} className="fu">
            <div style={{marginBottom:"12px"}}>
              <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:900,fontSize:".95rem",color:"var(--w)",marginBottom:"3px"}}>{homeData.mapAddress||"#578, 11th Cross, 7th Main, Vinayaka Layout, Nagharbhavi, Blr-72"}</div>
              <div style={{display:"flex",alignItems:"center",gap:"12px",flexWrap:"wrap",marginTop:"6px"}}>
                <span style={{fontSize:".76rem",color:"var(--t2)",display:"flex",alignItems:"center",gap:"5px"}}>📞 {homeData.phone||"9113587199"}</span>
                <span style={{fontSize:".76rem",color:"var(--t2)",display:"flex",alignItems:"center",gap:"5px"}}>🕐 {homeData.timings||"Mon–Sat: 8:00 AM – 5:00 PM"}</span>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px",marginBottom:"0"}}>
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(homeData.mapAddress||"Nagharbhavi Bangalore")}`} target="_blank" rel="noreferrer" style={{display:"flex",flexDirection:"column",alignItems:"center",gap:"5px",padding:"12px 8px",background:"linear-gradient(135deg,#f5a800,#ffd050)",borderRadius:"12px",textDecoration:"none",color:"#050e22",fontWeight:700,fontSize:".75rem"}}>
                <span style={{fontSize:"1.3rem"}}>🗺️</span>Directions
              </a>
              <a href={`https://maps.google.com/?q=${encodeURIComponent(homeData.mapAddress||"Nagharbhavi Bangalore")}`} target="_blank" rel="noreferrer" style={{display:"flex",flexDirection:"column",alignItems:"center",gap:"5px",padding:"12px 8px",background:"rgba(79,142,247,.15)",border:"1px solid rgba(79,142,247,.3)",borderRadius:"12px",textDecoration:"none",color:"var(--blue)",fontWeight:700,fontSize:".75rem"}}>
                <span style={{fontSize:"1.3rem"}}>📍</span>Open Maps
              </a>
              <a href={`tel:${homeData.phone||"9113587199"}`} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:"5px",padding:"12px 8px",background:"rgba(34,211,200,.12)",border:"1px solid rgba(34,211,200,.25)",borderRadius:"12px",textDecoration:"none",color:"var(--teal)",fontWeight:700,fontSize:".75rem"}}>
                <span style={{fontSize:"1.3rem"}}>📞</span>Call Us
              </a>
              <a href={`https://wa.me/91${String(homeData.phone||"9113587199").replace(/[^0-9]/g,"")}`} target="_blank" rel="noreferrer" style={{display:"flex",flexDirection:"column",alignItems:"center",gap:"5px",padding:"12px 8px",background:"rgba(37,211,102,.12)",border:"1px solid rgba(37,211,102,.25)",borderRadius:"12px",textDecoration:"none",color:"#25d366",fontWeight:700,fontSize:".75rem"}}>
                <span style={{fontSize:"1.3rem"}}>💬</span>WhatsApp
              </a>
            </div>
          </div>

          {/* Copyright Footer */}
          <div style={{textAlign:"center",padding:"16px 0 4px",borderTop:"1px solid rgba(255,255,255,.06)"}}>
            <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:700,fontSize:".8rem",color:"var(--gold)",marginBottom:"3px"}}>V Learn Tuition Centre</div>
            <div style={{fontSize:".65rem",color:"var(--t3)"}}>Tharika.P (B.Sc, B.Ed) · Nagharbhavi, Bangalore</div>
            <div style={{fontSize:".6rem",color:"var(--t3)",marginTop:"4px"}}>© {new Date().getFullYear()} V Learn. All rights reserved.</div>
          </div>
        </>)}

        {page==="marks"&&(<div className="fu">
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"16px"}}>
            <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:900,fontSize:"1rem",color:"var(--w)"}}>Recent Test Results</div>
            <span style={{fontSize:".72rem",color:"var(--t2)"}}>{child.tests?.length||0} tests</span>
          </div>
          {(!child.tests||child.tests.length===0)&&<div style={{background:"var(--card)",border:"1px solid var(--bdr)",borderRadius:"14px",padding:"40px",textAlign:"center"}}><div style={{fontSize:"2rem",marginBottom:"8px"}}>📝</div><div style={{color:"var(--t2)",fontSize:".85rem"}}>No tests recorded yet</div></div>}
          {(child.tests||[]).map((t,i)=>{
            const pct=Math.round(t.marks/t.total*100);
            const clr=pct>=90?"#22d3c8":pct>=80?"#22d3c8":pct>=70?"#f5a800":pct>=60?"#f5a800":"#f4546a";
            const grade=pct>=90?"A+":pct>=80?"A":pct>=70?"B+":pct>=60?"B":pct>=50?"C":"D";
            const gradeBg=pct>=80?"rgba(34,211,200,.12)":pct>=60?"rgba(245,168,0,.12)":"rgba(244,84,106,.12)";
            return(<div key={t.id||i} style={{background:"var(--card)",border:"1px solid rgba(255,255,255,.08)",borderRadius:"16px",padding:"18px",marginBottom:"14px",overflow:"hidden",position:"relative"}}>
              <div style={{position:"absolute",top:0,left:0,right:0,height:"3px",background:clr,borderRadius:"16px 16px 0 0"}}/>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:"12px"}}>
                <div style={{flex:1}}>
                  <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:900,fontSize:"1rem",color:"var(--w)",marginBottom:"4px"}}>{t.name}</div>
                  <div style={{display:"flex",alignItems:"center",gap:"8px",flexWrap:"wrap"}}>
                    <span style={{fontSize:".7rem",color:"var(--t2)",display:"flex",alignItems:"center",gap:"4px"}}>📅 {t.date}</span>
                    {t.subject&&<span style={{fontSize:".7rem",color:"var(--t2)"}}>• {t.subject}</span>}
                  </div>
                </div>
                <div style={{textAlign:"right",marginLeft:"12px"}}>
                  <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:900,fontSize:"2rem",color:clr,lineHeight:1}}>{t.marks}<span style={{fontSize:"1rem",color:"var(--t2)",fontWeight:600}}>/{t.total}</span></div>
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"12px"}}>
                <div style={{background:gradeBg,border:`1px solid ${clr}44`,borderRadius:"20px",padding:"3px 12px",fontFamily:"'Nunito',sans-serif",fontWeight:800,fontSize:".8rem",color:clr}}>{pct}%</div>
                <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:800,fontSize:".85rem",color:clr}}>Grade {grade}</div>
              </div>
              <div style={{background:"rgba(255,255,255,.05)",borderRadius:"8px",height:"8px",overflow:"hidden",marginBottom:"10px"}}>
                <div style={{height:"100%",width:`${pct}%`,background:`linear-gradient(90deg,${clr},${clr}99)`,borderRadius:"8px",transition:"width .6s ease"}}/>
              </div>
              {t.remark&&<div style={{background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)",borderRadius:"10px",padding:"10px 12px",display:"flex",alignItems:"center",gap:"8px"}}>
                <span style={{fontSize:"1rem"}}>💬</span>
                <span style={{fontSize:".78rem",color:"var(--t1)",fontStyle:"italic"}}>Teacher: "{t.remark}"</span>
              </div>}
            </div>);
          })}
        </div>)}

        {page==="attendance"&&<AttendanceView child={child}/>}

        {page==="fees"&&(<div className="fu">
          <div className="card" style={{marginBottom:"16px"}}><div className="ch"><span className="ct">Fee Status</span><span className={`bd ${feeClr(child.feeStatus)}`}>{child.feeStatus}</span></div>
            <div className="cb">
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:"14px"}}>
                <span style={{fontSize:".82rem",color:"var(--t2)"}}>Monthly Fee</span>
                <span style={{fontWeight:800,color:"var(--gold)",fontFamily:"'Nunito',sans-serif"}}>₹{fee?.toLocaleString()||"—"}</span>
              </div>
              {child.feeHistory?.map((f,i)=>(<div key={i} className="att-row"><div style={{flex:1,fontSize:".82rem",fontWeight:600}}>{f.month}</div><div style={{fontSize:".82rem",color:"var(--gold)",fontWeight:700}}>₹{f.amt?.toLocaleString()}</div><span className={`bd ${feeClr(f.status)}`}>{f.status}</span></div>))}
            </div>
          </div>
        </div>)}

        {page==="notices"&&<div className="fu"><NoticeCards notices={notices}/></div>}
        {page==="schedule"&&<SchedulePage homeData={homeData} setHomeData={()=>{}} isAdmin={false} addToast={()=>{}}/>}
        {page==="gallery_p"&&(<div className="fu">
          <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:800,fontSize:"1rem",color:"var(--w)",marginBottom:"14px"}}>📸 Centre Gallery</div>
          <div className="gallery">{(homeData.images||[]).map((img,i)=>(<div key={img.id||i} className="gimg"><img src={img.url} alt={img.caption} onError={e=>{e.target.src="https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=400&q=60";}}/><div className="gcap">{img.caption}</div></div>))}</div>
          {(!homeData.images||homeData.images.length===0)&&<div style={{color:"var(--t2)",textAlign:"center",padding:"40px",fontSize:".85rem"}}>No gallery images yet</div>}
        </div>)}
        {page==="map_p"&&(<div className="fu">
          <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:800,fontSize:"1rem",color:"var(--w)",marginBottom:"14px"}}>📍 Our Location</div>
          <div className="card" style={{marginBottom:"14px"}}><div className="ch"><span className="ct">Address</span></div><div className="cb"><div style={{fontSize:".85rem",color:"var(--t1)",lineHeight:1.6}}>{homeData.mapAddress||"#578, 11th Cross, 7th Main, Vinayaka Layout, Nagharbhavi, Blr-72"}</div><div style={{marginTop:"10px"}}><a href={`tel:${homeData.phone||"9113587199"}`} style={{color:"var(--gold)",fontWeight:800,fontSize:".9rem",textDecoration:"none"}}>📞 {homeData.phone||"9113587199"}</a></div><div style={{fontSize:".76rem",color:"var(--t2)",marginTop:"4px"}}>🕐 {homeData.timings||"Mon–Sat: 8:00 AM – 5:00 PM"}</div></div></div>
          <div className="map-card"><iframe className="map-frame" title="Map" src={homeData.lat&&homeData.lng?`https://maps.google.com/maps?q=${homeData.lat},${homeData.lng}&z=17&output=embed`:`https://maps.google.com/maps?q=${encodeURIComponent(homeData.mapAddress||"Nagharbhavi Bangalore")}&z=16&output=embed`} allowFullScreen loading="lazy"/></div>
        </div>)}
      </div>
    </div>
    <BottomTabs tabs={btabs} page={page} setPage={setPage}/>
  </div>);
}

/* ══════ MAIN APP (Admin + Teacher) ══════ */
function MainApp({user,students,setStudents,notices,setNotices,feeStruct,setFeeStruct,homeData,setHomeData,allUsers,setAllUsers,enquiries,setEnquiries,onLogout,addToast}){
  const isAdmin=user.role==="admin";
  const[page,setPage]=useState("dashboard");
  const[sbOpen,setSbOpen]=useState(false);
  const[stab,setStab]=useState("all");
  const[showEnrol,setShowEnrol]=useState(false);
  const[editStudent,setEditStudent]=useState(null);
  const[showMarks,setShowMarks]=useState(false);
  const[showAttModal,setShowAttModal]=useState(false);
  const[attRecords,setAttRecords]=useState({});
  const[saving,setSaving]=useState(false);
  const[stats,setStats]=useState(null);

  useEffect(()=>{api.getStats().then(setStats).catch(()=>{});},[students,enquiries]);

  const pending=allUsers.filter(u=>!u.approved&&u.role!=="admin");
  const newEnq=enquiries.filter(e=>e.status==="new").length;

  const adminNav=[
    {label:"Main",items:[{id:"dashboard",ic:"📊",label:"Dashboard",icBg:"linear-gradient(135deg,#4f8ef7,#82b0ff)"},{id:"students",ic:"👨‍🎓",label:"Students",badge:0,icBg:"linear-gradient(135deg,#f5a800,#ffd050)"}]},
    {label:"Academic",items:[{id:"attendance",ic:"📅",label:"Attendance",icBg:"linear-gradient(135deg,#22d3c8,#5ef5ee)"},{id:"marks_entry",ic:"📝",label:"Marks Entry",icBg:"linear-gradient(135deg,#a78bfa,#c4b5fd)"}]},
    {label:"Admin",items:[
      {id:"fees",ic:"💰",label:"Fees",icBg:"linear-gradient(135deg,#22c55e,#4ade80)"},
      {id:"notices_view",ic:"📢",label:"Notices",icBg:"linear-gradient(135deg,#f4546a,#f89ba5)"},
      {id:"enquiries",ic:"📋",label:"Enquiries",icBg:"linear-gradient(135deg,#ff6b35,#ff9a76)",badge:newEnq},
      {id:"schedule",ic:"🗓️",label:"Schedule",icBg:"linear-gradient(135deg,#06b6d4,#38bdf8)"},
      {id:"cms",ic:"⚙️",label:"Settings",icBg:"linear-gradient(135deg,#64748b,#94a3b8)",badge:pending.length},
    ]},
  ];
  const teacherNav=[
    {label:"Main",items:[{id:"dashboard",ic:"📊",label:"Dashboard",icBg:"linear-gradient(135deg,#4f8ef7,#82b0ff)"},{id:"students",ic:"👨‍🎓",label:"Students",icBg:"linear-gradient(135deg,#f5a800,#ffd050)"}]},
    {label:"Academic",items:[{id:"attendance",ic:"📅",label:"Attendance",icBg:"linear-gradient(135deg,#22d3c8,#5ef5ee)"},{id:"marks_entry",ic:"📝",label:"Marks Entry",icBg:"linear-gradient(135deg,#a78bfa,#c4b5fd)"}]},
    {label:"Info",items:[{id:"notices_view",ic:"📢",label:"Notices",icBg:"linear-gradient(135deg,#f4546a,#f89ba5)"},{id:"schedule",ic:"🗓️",label:"Schedule",icBg:"linear-gradient(135deg,#06b6d4,#38bdf8)"}]},
  ];
  const adminBtabs=[{id:"dashboard",ic:"📊",label:"Dash",icBg:"linear-gradient(135deg,#4f8ef7,#82b0ff)"},{id:"students",ic:"👨‍🎓",label:"Students",icBg:"linear-gradient(135deg,#f5a800,#ffd050)"},{id:"attendance",ic:"📅",label:"Attend.",icBg:"linear-gradient(135deg,#22d3c8,#5ef5ee)"},{id:"marks_entry",ic:"📝",label:"Marks",icBg:"linear-gradient(135deg,#a78bfa,#c4b5fd)"},{id:"cms",ic:"⚙️",label:"CMS",icBg:"linear-gradient(135deg,#64748b,#94a3b8)",badge:pending.length+newEnq}];
  const teacherBtabs=[{id:"dashboard",ic:"📊",label:"Dash",icBg:"linear-gradient(135deg,#4f8ef7,#82b0ff)"},{id:"students",ic:"👨‍🎓",label:"Students",icBg:"linear-gradient(135deg,#f5a800,#ffd050)"},{id:"attendance",ic:"📅",label:"Attend.",icBg:"linear-gradient(135deg,#22d3c8,#5ef5ee)"},{id:"marks_entry",ic:"📝",label:"Marks",icBg:"linear-gradient(135deg,#a78bfa,#c4b5fd)"},{id:"notices_view",ic:"📢",label:"Notices",icBg:"linear-gradient(135deg,#f4546a,#f89ba5)"}];

  const filtStu=students.filter(s=>stab==="all"?true:s.feeStatus?.toLowerCase()===stab);
  const ptitle={dashboard:"Dashboard",students:"Students",attendance:"Attendance",marks_entry:"Marks Entry",fees:"Fee Management",notices_view:"Notices",enquiries:"Enquiries",schedule:"Schedule",cms:"Settings"};

  const collectedAmt=students.filter(s=>s.feeStatus==="Paid").reduce((a,s)=>a+(feeStruct[s.grade]?.[s.subject]||0),0);
  const pendingAmt=students.filter(s=>s.feeStatus==="Pending").reduce((a,s)=>a+(feeStruct[s.grade]?.[s.subject]||0),0);
  const overdueAmt=students.filter(s=>s.feeStatus==="Overdue").reduce((a,s)=>a+(feeStruct[s.grade]?.[s.subject]||0),0);

  const handleEnrol=async(f)=>{
    if(!f.name||!f.grade||!f.subject)return addToast("Fill required fields","err","⚠");
    setSaving(true);
    try{const s=await api.addStudent(f);setStudents(p=>[...p,s]);setShowEnrol(false);addToast(`${s.name} enrolled! 🎉`,"mark","✓");}
    catch(e){addToast(e.message,"err","⚠");}
    finally{setSaving(false);}
  };

  const handleEdit=async(f)=>{
    setSaving(true);
    try{const s=await api.updateStudent(f.id,f);setStudents(p=>p.map(x=>x.id===s.id?s:x));setEditStudent(null);addToast("Student updated ✓");}
    catch(e){addToast(e.message,"err","⚠");}
    finally{setSaving(false);}
  };

  const handleDelete=async(s)=>{
    if(!window.confirm(`Delete ${s.name}? This cannot be undone.`))return;
    try{await api.deleteStudent(s.id);setStudents(p=>p.filter(x=>x.id!==s.id));addToast(`${s.name} removed`);}
    catch(e){addToast(e.message,"err","⚠");}
  };

  const handleMarkAttendance=async()=>{
    // Get selected date from picker (supports backdating)
    const picker=document.getElementById("attDatePicker");
    const selectedDate=picker?.value
      ? new Date(picker.value).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}).replace(/ /g,"-")
      : todayStr();
    const records=students.map(s=>({student_id:s.id,date:selectedDate,status:attRecords[s.id]||"present"}));
    setSaving(true);
    try{
      await api.markAttendance(records);
      const upd=await api.getStudents();
      setStudents(upd);
      setShowAttModal(false);
      setAttRecords({});
      addToast(`Attendance saved for ${selectedDate} ✅`,"mark","✅");
    }
    catch(e){addToast(e.message,"err","⚠");}
    finally{setSaving(false);}
  };

  const markFeePaid=async(s)=>{
    try{await api.updateFeeStatus(s.id,"Paid");setStudents(p=>p.map(x=>x.id===s.id?{...x,feeStatus:"Paid"}:x));addToast(`${s.name} fee marked Paid`);}
    catch(e){addToast(e.message,"err","⚠");}
  };

  return(<div className="shell">
    <Sidebar user={user} page={page} setPage={setPage} nav={isAdmin?adminNav:teacherNav} onLogout={onLogout} isOpen={sbOpen} onClose={()=>setSbOpen(false)} homeData={homeData}/>
    <div className="main">
      <div className="topbar">
        <button className="menu-btn" onClick={()=>setSbOpen(!sbOpen)}>☰</button>
        <div className="tb-t"><h2>{ptitle[page]}</h2><p><span className="live-dot"></span>Real-time · {todayStr()}</p></div>
        <div className="tb-a">
          {(page==="students")&&<button className="btn bp" onClick={()=>setShowEnrol(true)}>+ Enrol Student</button>}
          {page==="attendance"&&<button className="btn bp" onClick={()=>setShowAttModal(true)}>📅 Mark Today</button>}
        </div>
      </div>
      <div className="content">

        {/* ─── DASHBOARD ─── */}
        {page==="dashboard"&&(<>
          <div className="sg sg4 fu">
            {[{cl:"c1",ic:"👨‍🎓",lb:"Students",val:stats?.totalStudents??students.length,sub:"Enrolled"},
              {cl:"c2",ic:"✅",lb:"Today Present",val:stats?.todayPresent??"—",sub:"Students"},
              {cl:"c3",ic:"📊",lb:"Avg Attendance",val:`${stats?.avgAtt??Math.round(students.reduce((a,s)=>a+s.att,0)/(students.length||1))}%`,sub:"Overall"},
              {cl:"c4",ic:"📋",lb:"New Enquiries",val:stats?.newEnquiries??newEnq,sub:"Unread"},
            ].map((s,i)=>(<div key={i} className={`sc ${s.cl}`}><div className="si">{s.ic}</div><div className="slbl">{s.lb}</div><div className="sval" style={{fontSize:"1.5rem"}}>{s.val}</div><div className="ssub">{s.sub}</div></div>))}
          </div>
          {pending.length>0&&<div className="card fu2" style={{marginBottom:"16px",border:"1px solid rgba(245,168,0,.3)"}}>
            <div className="ch" style={{background:"rgba(245,168,0,.06)"}}><span className="ct">⏳ {pending.length} Pending Approval{pending.length>1?"s":""}</span><button className="btn bp" style={{padding:"5px 12px",fontSize:".74rem"}} onClick={()=>setPage("cms")}>Review →</button></div>
            <div className="cb" style={{padding:"8px 18px"}}>{pending.slice(0,3).map((u,i)=>(<div key={u.id} style={{display:"flex",alignItems:"center",gap:"10px",padding:"7px 0",borderBottom:i<pending.length-1?"1px solid rgba(255,255,255,.05)":"none"}}><div className={`av ${getAv(i)}`}>{ini(u.name)}</div><div style={{flex:1}}><div style={{fontSize:".82rem",fontWeight:600}}>{u.name}</div><div style={{fontSize:".67rem",color:"var(--t2)"}}>{u.role} · {u.email}</div></div><span className="bd bo">Pending</span></div>))}</div>
          </div>}
          <div className="gm fu3">
            <div className="card"><div className="ch"><span className="ct">Students</span><span className="bd bg3"><span className="live-dot" style={{marginRight:"4px"}}></span>Live</span></div>
              <div style={{padding:"0 18px"}}><div className="tbl-wrap"><table className="tbl"><thead><tr><th>Student</th><th>Grade</th><th>Attend.</th><th>Fee</th></tr></thead>
                <tbody>{students.slice(0,6).map((s,i)=>(<tr key={s.id}><td><div className="nc"><div className={`av ${getAv(i)}`}>{ini(s.name)}</div>{s.name}</div></td><td><span className="chip">{s.grade}</span></td><td><span style={{fontWeight:700,color:attClr(s.att)}}>{s.att}%</span></td><td><span className={`bd ${feeClr(s.feeStatus)}`}>{s.feeStatus}</span></td></tr>))}</tbody>
              </table></div></div>
            </div>
            <div><div className="card" style={{marginBottom:"12px"}}><div className="ch"><span className="ct">💰 Fees</span></div><div className="cb">
              {[{l:"Collected",v:`₹${collectedAmt.toLocaleString()}`,c:"var(--teal)"},{l:"Pending",v:`₹${pendingAmt.toLocaleString()}`,c:"var(--gold)"},{l:"Overdue",v:`₹${overdueAmt.toLocaleString()}`,c:"var(--rose)"}].map((r,i)=>(<div key={i} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid var(--bdr)"}}><span style={{fontSize:".82rem",color:"var(--t2)"}}>{r.l}</span><span style={{fontWeight:800,color:r.c,fontFamily:"'Nunito',sans-serif"}}>{r.v}</span></div>))}
            </div></div>
            <div className="card"><div className="ch"><span className="ct">📢 Notices</span></div><div className="cb" style={{padding:"8px 14px"}}><NoticeCards notices={notices.slice(0,2)}/></div></div>
            </div>
          </div>
        </>)}

        {/* ─── STUDENTS ─── */}
        {page==="students"&&(<>
          <div className="tabs">{["all","paid","pending","overdue"].map(t=><button key={t} className={`tab${stab===t?" active":""}`} onClick={()=>setStab(t)}>{t}</button>)}</div>
          <div className="card fu">
            <div className="ch"><span className="ct">{filtStu.length} Students</span><div style={{display:"flex",gap:"6px"}}></div></div>
            <div style={{padding:"0 18px"}}><div className="tbl-wrap"><table className="tbl">
              <thead><tr><th>Student</th><th>Grade</th><th>Subject</th><th>Parent</th><th>Fee/mo</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>{filtStu.map((s,i)=>{
                const lf=feeStruct[s.grade]?.[s.subject];
                return(<tr key={s.id}>
                  <td><div className="nc"><div className={`av ${getAv(i)}`}>{ini(s.name)}</div>{s.name}</div></td>
                  <td><span className="chip">{s.grade}</span></td>
                  <td style={{fontSize:".76rem",color:"var(--t2)"}}>{s.subject}</td>
                  <td style={{fontSize:".76rem",color:"var(--t2)"}}>{s.parent_name||"—"}</td>
                  <td style={{fontWeight:600,color:"var(--gold)"}}>{lf?`₹${lf.toLocaleString()}`:"—"}</td>
                  <td><span className={`bd ${feeClr(s.feeStatus)}`}>{s.feeStatus}</span></td>
                  <td><div style={{display:"flex",gap:"4px"}}>
                    <button className="btn bbl" style={{padding:"3px 8px",fontSize:".68rem"}} onClick={()=>setEditStudent(s)}>✏️ Edit</button>
                    <button className="btn br2" style={{padding:"3px 8px",fontSize:".68rem"}} onClick={()=>handleDelete(s)}>🗑️</button>
                  </div></td>
                </tr>);
              })}</tbody>
            </table></div></div>
          </div>
        </>)}

        {/* ─── MARKS ENTRY ─── */}
        {page==="marks_entry"&&<MarksHistoryPage students={students} setStudents={setStudents} onAddMarks={()=>setShowMarks(true)} addToast={addToast}/>}

        {/* ─── ATTENDANCE ─── */}
        {page==="attendance"&&<AdminAttendancePage students={students} addToast={addToast}/>}

        {/* ─── FEES ─── */}
        {page==="fees"&&(<>
          <div className="sg sg3">{[{cl:"c2",ic:"💰",lb:"Collected",val:`₹${collectedAmt.toLocaleString()}`,sub:`${students.filter(s=>s.feeStatus==="Paid").length} students`},{cl:"c1",ic:"⏳",lb:"Pending",val:`₹${pendingAmt.toLocaleString()}`,sub:`${students.filter(s=>s.feeStatus==="Pending").length} students`},{cl:"c4",ic:"⚠️",lb:"Overdue",val:`₹${overdueAmt.toLocaleString()}`,sub:`${students.filter(s=>s.feeStatus==="Overdue").length} students`}].map((s,i)=>(<div key={i} className={`sc ${s.cl} fu`}><div className="si">{s.ic}</div><div className="slbl">{s.lb}</div><div className="sval" style={{fontSize:"1.4rem"}}>{s.val}</div><div className="ssub">{s.sub}</div></div>))}</div>
          <div className="card fu2"><div className="ch"><span className="ct">Fee Records</span></div>
            <div style={{padding:"0 18px"}}><div className="tbl-wrap"><table className="tbl"><thead><tr><th>Student</th><th>Grade</th><th>Fee/mo</th><th>Status</th><th>Action</th></tr></thead>
              <tbody>{students.map((s,i)=>{const lf=feeStruct[s.grade]?.[s.subject];return(<tr key={s.id}><td><div className="nc"><div className={`av ${getAv(i)}`}>{ini(s.name)}</div>{s.name}</div></td><td><span className="chip">{s.grade}</span></td><td style={{fontWeight:700,color:"var(--gold)"}}>{lf?`₹${lf.toLocaleString()}`:"—"}</td><td><span className={`bd ${feeClr(s.feeStatus)}`}>{s.feeStatus}</span></td><td><select style={{background:"rgba(255,255,255,.06)",border:"1px solid var(--bdr)",borderRadius:"7px",color:"var(--w)",fontFamily:"'DM Sans',sans-serif",fontSize:".72rem",padding:"4px 8px",cursor:"pointer",outline:"none"}}
  value={s.feeStatus||s.fee_status||"Pending"}
  onChange={async e=>{
    const newStatus=e.target.value;
    try{
      await api.updateFeeStatus(s.id,newStatus);
      setStudents(p=>p.map(x=>x.id===s.id?{...x,feeStatus:newStatus,fee_status:newStatus}:x));
      addToast(`${s.name} → ${newStatus}`);
    }catch(err){addToast(err.message,"err","⚠");}
  }}>
  <option value="Paid">✓ Paid</option>
  <option value="Pending">⏳ Pending</option>
  <option value="Overdue">⚠️ Overdue</option>
</select></td></tr>);})}</tbody>
            </table></div></div>
          </div>
        </>)}

        {/* ─── NOTICES ─── */}
        {page==="notices_view"&&(<div className="g2">
          <div><div style={{fontFamily:"'Nunito',sans-serif",fontWeight:800,fontSize:"1rem",color:"var(--w)",marginBottom:"12px"}}>All Notices</div><NoticeCards notices={notices}/></div>
          <div className="card fu2"><div className="ch"><span className="ct">Schedule</span></div><div className="cb" style={{padding:"6px 18px"}}>{(homeData.schedule||[]).map((s,i)=><SI key={i} s={s} showDay/>)}</div></div>
        </div>)}

        {/* ─── ENQUIRIES ─── */}
        {page==="enquiries"&&isAdmin&&(<EnquiriesPanel enquiries={enquiries} setEnquiries={setEnquiries} addToast={addToast} feeStruct={feeStruct}/>)}

        {/* ─── SCHEDULE ─── */}
        {page==="schedule"&&<SchedulePage homeData={homeData} setHomeData={setHomeData} isAdmin={isAdmin} addToast={addToast}/>}

        {/* ─── ADMIN CMS ─── */}
        {page==="cms"&&isAdmin&&(<AdminCMS homeData={homeData} setHomeData={setHomeData} notices={notices} setNotices={setNotices} feeStruct={feeStruct} setFeeStruct={setFeeStruct} allUsers={allUsers} setAllUsers={setAllUsers} students={students} setStudents={setStudents} addToast={addToast}/>)}
      </div>
    </div>
    <BottomTabs tabs={isAdmin?adminBtabs:teacherBtabs} page={page} setPage={setPage}/>

    {/* ENROL MODAL */}
    {showEnrol&&(<div className="ov"><div className="modal"><div className="mh"><h3>➕ Enrol New Student</h3><button className="cls" onClick={()=>setShowEnrol(false)}>✕</button></div><StudentForm feeStruct={feeStruct} allUsers={allUsers} onCancel={()=>setShowEnrol(false)} onSave={handleEnrol} saving={saving} isAdmin={isAdmin}/></div></div>)}

    {/* EDIT MODAL */}
    {editStudent&&(<div className="ov"><div className="modal"><div className="mh"><h3>✏️ Edit Student</h3><button className="cls" onClick={()=>setEditStudent(null)}>✕</button></div><StudentForm feeStruct={feeStruct} allUsers={allUsers} initial={editStudent} onCancel={()=>setEditStudent(null)} onSave={handleEdit} saving={saving} isAdmin={isAdmin}/></div></div>)}

    {/* MARKS MODAL */}
    {showMarks&&(<div className="ov"><div className="modal"><div className="mh"><h3>📝 Add Marks</h3><button className="cls" onClick={()=>setShowMarks(false)}>✕</button></div>
      <MarksForm students={students} onCancel={()=>setShowMarks(false)} onSave={async(d)=>{
        try{const t=await api.addTest(d);setStudents(p=>p.map(s=>s.id===d.student_id?{...s,tests:[t,...(s.tests||[])]}:s));setShowMarks(false);addToast("Marks saved! Parent notified 📊","mark","✓");}
        catch(e){addToast(e.message,"err","⚠");}
      }}/>
    </div></div>)}

    {/* ATTENDANCE MODAL */}
    {showAttModal&&(<div className="ov"><div className="modal">
      <div className="mh"><h3>📅 Mark Attendance</h3><button className="cls" onClick={()=>setShowAttModal(false)}>✕</button></div>
      <div className="fg" style={{marginBottom:"12px"}}>
        <label className="fl">Date</label>
        <input className="fi" type="date"
          defaultValue={new Date().toISOString().split("T")[0]}
          id="attDatePicker"
          style={{fontSize:".9rem"}}
        />
        <div style={{fontSize:".65rem",color:"var(--t2)",marginTop:"3px"}}>You can select today or any previous date to add/correct attendance</div>
      </div>
      <div style={{marginBottom:"16px"}}>
        <div style={{display:"flex",gap:"8px",marginBottom:"12px"}}>
          <button className="btn bgr" style={{fontSize:".72rem",padding:"5px 10px"}} onClick={()=>setAttRecords(Object.fromEntries(students.map(s=>[s.id,"present"])))}>✓ All Present</button>
          <button className="btn br2" style={{fontSize:".72rem",padding:"5px 10px"}} onClick={()=>setAttRecords(Object.fromEntries(students.map(s=>[s.id,"absent"])))}>✗ All Absent</button>
        </div>
        {students.map((s,i)=>{const cur=attRecords[s.id]||"present";return(<div key={s.id} className="att-row">
          <div className={`av ${getAv(i)}`}>{ini(s.name)}</div>
          <div style={{flex:1}}><div style={{fontSize:".82rem",fontWeight:600}}>{s.name}</div><div style={{fontSize:".67rem",color:"var(--t2)"}}>{s.grade}</div></div>
          <div style={{display:"flex",gap:"5px"}}>
            {["present","absent","late"].map(st=>(<button key={st} className={`att-btn${cur===st?st==="present"?" att-p-active":st==="absent"?" att-a-active":" att-l-active":""}`} onClick={()=>setAttRecords(p=>({...p,[s.id]:st}))}>{st[0].toUpperCase()}</button>))}
          </div>
        </div>);})}
      </div>
      <div className="ma"><button className="btn bg2" onClick={()=>setShowAttModal(false)}>Cancel</button><button className="btn bp" onClick={handleMarkAttendance} disabled={saving}>{saving?"Saving…":"✓ Save All"}</button></div>
    </div></div>)}
  </div>);
}

/* ══════ STUDENT FORM (Enrol + Edit) ══════ */

/* ══════ SCHEDULE PAGE ══════ */
const SLOT_COLORS=["#f5a800","#22d3c8","#4f8ef7","#f4546a","#a78bfa","#22c55e","#ff6b35","#c084fc"];
const DAYS=["Mon","Tue","Wed","Thu","Fri","Sat","Mon–Sat","Daily"];

function SchedulePage({homeData,setHomeData,isAdmin,addToast}){
  const[editMode,setEditMode]=useState(false);
  const[saving,setSaving]=useState(false);
  // Load fresh schedule on mount
  useEffect(()=>{
    api.getHome().then(d=>{if(d.schedule)setHomeData(h=>({...h,schedule:d.schedule}));}).catch(()=>{});
  },[setHomeData]);
  // NO stale `slots` constant - always read homeData.schedule directly
  const updateSlot=(i,k,v)=>{
    setHomeData(h=>{const sc=[...(h.schedule||[])];sc[i]={...sc[i],[k]:v};return{...h,schedule:sc};});
  };
  const removeSlot=(i)=>setHomeData(h=>({...h,schedule:(h.schedule||[]).filter((_,j)=>j!==i)}));
  const addSlot=()=>setHomeData(h=>{const sc=h.schedule||[];return{...h,schedule:[...sc,{time:"",name:"",meta:"",room:"Room A",color:SLOT_COLORS[sc.length%SLOT_COLORS.length],day:"Mon"}]};});
  const saveSchedule=async()=>{
    const currentSlots=homeData.schedule||[]; // read current state at save time
    setSaving(true);
    try{
      await api.updateHome({
        heroTitle:homeData.heroTitle,heroSubtitle:homeData.heroSubtitle,
        heroDesc:homeData.heroDesc,phone:homeData.phone,timings:homeData.timings,
        mapAddress:homeData.mapAddress,
        features:Array.isArray(homeData.features)?homeData.features:[],
        schedule:currentSlots,
        lat:homeData.lat||"",lng:homeData.lng||""
      });
      // Reload homeData from server to confirm save
      const fresh=await api.getHome();
      if(fresh.schedule)setHomeData(h=>({...h,schedule:fresh.schedule}));
      addToast(`Schedule saved! ${currentSlots.length} slots published ✓`,"mark","✓");
      setEditMode(false);
    }
    catch(e){addToast(e.message||"Save failed — check connection","err","⚠");}
    finally{setSaving(false);}
  };

  return(<div className="fu">
    {/* Header */}
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"16px",flexWrap:"wrap",gap:"10px"}}>
      <div>
        <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:900,fontSize:"1.1rem",color:"var(--w)"}}>Class Schedule</div>
        <div style={{fontSize:".72rem",color:"var(--t2)",marginTop:"2px"}}>{(homeData.schedule||[]).length} slots · visible to all teachers & parents</div>
      </div>
      {isAdmin&&<div style={{display:"flex",gap:"8px"}}>
        {editMode?(<>
          <button className="btn bg2" onClick={()=>setEditMode(false)} style={{padding:"7px 14px"}}>✕ Cancel</button>
          <button className="btn bp" onClick={saveSchedule} disabled={saving} style={{padding:"7px 16px"}}>{saving?"Saving…":"💾 Save Schedule"}</button>
        </>):(
          <button className="btn bp" onClick={()=>setEditMode(true)} style={{padding:"7px 16px"}}>✏️ Edit Schedule</button>
        )}
      </div>}
    </div>

    {/* Edit Mode */}
    {isAdmin&&editMode&&(<>
      <div style={{background:"rgba(79,142,247,.08)",border:"1px solid rgba(79,142,247,.2)",borderRadius:"10px",padding:"10px 14px",marginBottom:"14px",fontSize:".76rem",color:"var(--t2)"}}>
        ℹ️ Add, edit or remove class slots. Click <strong style={{color:"var(--gold)"}}>Save</strong> to publish to all users.
      </div>
      {(homeData.schedule||[]).map((s,i)=>(<div key={i} style={{background:"var(--card)",border:`1px solid ${s.color||"#f5a800"}33`,borderLeft:`3px solid ${s.color||"#f5a800"}`,borderRadius:"12px",padding:"14px",marginBottom:"10px"}}>
        <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"12px"}}>
          <div style={{width:"28px",height:"28px",borderRadius:"7px",background:s.color||"#f5a800",display:"flex",alignItems:"center",justifyContent:"center",color:"#050e22",fontWeight:900,fontSize:".75rem",flexShrink:0}}>{i+1}</div>
          <span style={{fontWeight:700,color:"var(--w)",fontSize:".85rem",flex:1}}>{s.name||"New Slot"}</span>
          <button onClick={()=>removeSlot(i)} style={{background:"rgba(244,84,106,.12)",border:"1px solid rgba(244,84,106,.25)",borderRadius:"7px",color:"var(--rose)",padding:"3px 9px",cursor:"pointer",fontSize:".7rem",fontFamily:"'DM Sans',sans-serif",fontWeight:600}}>✕ Remove</button>
        </div>
        <div className="fr2" style={{marginBottom:"8px"}}>
          <div className="fg"><label className="fl">⏰ Time</label><input className="fi" placeholder="e.g. 8:00 AM" value={s.time} onChange={e=>updateSlot(i,"time",e.target.value)}/></div>
          <div className="fg"><label className="fl">📚 Subject</label><select className="fi" value={s.name} onChange={e=>updateSlot(i,"name",e.target.value)}><option value="">— Select Subject —</option>{ALL_SUBJECTS.map(sub=><option key={sub}>{sub}</option>)}</select></div>
        </div>
        <div className="fr2" style={{marginBottom:"8px"}}>
          <div className="fg"><label className="fl">🎓 Grade / Class</label><input className="fi" placeholder="e.g. Class 4 & 5" value={s.meta} onChange={e=>updateSlot(i,"meta",e.target.value)}/></div>
          <div className="fg"><label className="fl">🚪 Room</label><input className="fi" placeholder="Room A" value={s.room||""} onChange={e=>updateSlot(i,"room",e.target.value)}/></div>
        </div>
        <div className="fr2">
          <div className="fg"><label className="fl">📅 Day</label><select className="fi" value={s.day||"Mon"} onChange={e=>updateSlot(i,"day",e.target.value)}>{DAYS.map(d=><option key={d}>{d}</option>)}</select></div>
          <div className="fg"><label className="fl">🎨 Color</label><div style={{display:"flex",gap:"6px",marginTop:"4px",flexWrap:"wrap"}}>{SLOT_COLORS.map(c=><div key={c} onClick={()=>updateSlot(i,"color",c)} style={{width:"22px",height:"22px",borderRadius:"6px",background:c,cursor:"pointer",border:s.color===c?"2px solid #fff":"2px solid transparent",transition:"all .15s"}}/> )}</div></div>
        </div>
      </div>))}
      <div style={{display:"flex",gap:"10px",marginTop:"6px"}}>
        <button className="btn bg2" onClick={addSlot} style={{flex:1}}>+ Add Class Slot</button>
        <button className="btn bp" onClick={saveSchedule} disabled={saving} style={{flex:1}}>{saving?"Saving…":"💾 Save Schedule"}</button>
      </div>
    </>)}

    {/* View Mode */}
    {!editMode&&(<>
      {(homeData.schedule||[]).length===0?(<div style={{background:"var(--card)",border:"1px solid var(--bdr)",borderRadius:"14px",padding:"40px",textAlign:"center"}}>
        <div style={{fontSize:"2rem",marginBottom:"10px"}}>📅</div>
        <div style={{color:"var(--t2)",fontSize:".85rem",marginBottom:"14px"}}>{isAdmin?"No schedule yet. Click below to create one.":"No schedule set yet."}</div>
        {isAdmin&&<button className="btn bp" onClick={()=>{setEditMode(true);addSlot();}}>+ Create Schedule</button>}
      </div>):(
        <div style={{display:"grid",gap:"10px"}}>
          {(homeData.schedule||[]).map((s,i)=>(<div key={i} style={{background:"var(--card)",border:`1px solid ${s.color||"#f5a800"}22`,borderLeft:`4px solid ${s.color||"#f5a800"}`,borderRadius:"12px",padding:"14px 16px",display:"flex",alignItems:"center",gap:"14px"}}>
            <div style={{textAlign:"center",minWidth:"52px"}}>
              <div style={{fontSize:".72rem",fontWeight:700,color:s.color||"var(--gold)"}}>{ s.day||""}</div>
              <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:900,fontSize:".9rem",color:"var(--w)",marginTop:"1px"}}>{s.time}</div>
            </div>
            <div style={{width:"1px",height:"36px",background:"rgba(255,255,255,.08)",flexShrink:0}}/>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,fontSize:".88rem",color:"var(--w)"}}>{s.name||"—"}</div>
              <div style={{fontSize:".7rem",color:"var(--t2)",marginTop:"2px"}}>{s.meta}</div>
            </div>
            {s.room&&<span style={{fontSize:".65rem",padding:"3px 10px",borderRadius:"8px",background:`${s.color||"#4f8ef7"}15`,color:s.color||"var(--blue)",fontWeight:700,border:`1px solid ${s.color||"#4f8ef7"}30`,whiteSpace:"nowrap"}}>{s.room}</span>}
          </div>))}
        </div>
      )}
    </>)}
  </div>);
}

function StudentForm({feeStruct,allUsers,initial,onCancel,onSave,saving,isAdmin}){
  const[f,setF]=useState({name:"",grade:"",subject:"",parentName:"",phone:"",email:"",dob:"",parent_id:"",fee_status:"Pending",...(initial||{})});
  const up=(k,v)=>setF(p=>({...p,[k]:v}));
  const avSubs=ALL_SUBJECTS;
  const liveFee=f.grade&&f.subject?feeStruct[f.grade]?.[f.subject]:null;
  const parentUsers=allUsers.filter(u=>u.role==="parent"&&u.approved);

  return(<>
    <div className="fr2" style={{marginBottom:"12px"}}>
      <div className="fg"><label className="fl">Student Name *</label><input className="fi" placeholder="Full name" value={f.name} onChange={e=>up("name",e.target.value)}/></div>
      <div className="fg"><label className="fl">Date of Birth</label><input className="fi" type="date" value={f.dob} onChange={e=>up("dob",e.target.value)}/></div>
    </div>
    <div className="fr2" style={{marginBottom:"12px"}}>
      <div className="fg"><label className="fl">Class *</label><select className="fi" value={f.grade} onChange={e=>{up("grade",e.target.value);up("subject","");}}><option value="">— Select —</option>{GRADES.map(g=><option key={g}>{g}</option>)}</select></div>
      <div className="fg"><label className="fl">Subject *</label><select className="fi" value={f.subject} onChange={e=>up("subject",e.target.value)} disabled={!f.grade}><option value="">— Select —</option>{avSubs.map(s=><option key={s}>{s}</option>)}</select></div>
    </div>
    <div style={{marginBottom:"12px"}}>
      {liveFee!=null&&<div style={{background:"var(--gf)",border:"2px solid rgba(245,168,0,.4)",borderRadius:"10px",padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"8px"}}><span style={{fontSize:".8rem"}}>💰 Standard Fee</span><span style={{fontFamily:"'Nunito',sans-serif",fontWeight:900,color:"var(--gold)",fontSize:"1.2rem"}}>₹{liveFee.toLocaleString()}/mo</span></div>}
      <div className="fg" style={{margin:0}}><label className="fl">Custom Monthly Fee (override, optional)</label><div style={{display:"flex",alignItems:"center",gap:"6px"}}><span style={{color:"var(--gold)",fontWeight:700}}>₹</span><input className="fi" type="number" placeholder={liveFee?`Leave blank to use ₹${liveFee}`:"Enter custom fee amount"} value={f.custom_fee||""} onChange={e=>up("custom_fee",e.target.value?parseInt(e.target.value):"")} style={{flex:1}}/></div><div style={{fontSize:".65rem",color:"var(--t2)",marginTop:"3px"}}>Set a different fee for this student specifically</div></div>
    </div>
    <div className="fr2" style={{marginBottom:"12px"}}>
      <div className="fg"><label className="fl">Parent Name</label><input className="fi" placeholder="Parent / Guardian" value={f.parentName||f.parent_name||""} onChange={e=>up("parentName",e.target.value)}/></div>
      <div className="fg"><label className="fl">Phone</label><input className="fi" placeholder="91XXXXXXXX" value={f.phone} onChange={e=>up("phone",e.target.value)}/></div>
    </div>
    <div className="fg"><label className="fl">Email</label><input className="fi" type="email" placeholder="optional" value={f.email||""} onChange={e=>up("email",e.target.value)}/></div>
    {initial&&<div className="fg"><label className="fl">Fee Status</label><select className="fi" value={f.fee_status} onChange={e=>up("fee_status",e.target.value)}>{["Pending","Paid","Overdue"].map(s=><option key={s}>{s}</option>)}</select></div>}
    {isAdmin&&<div className="fg"><label className="fl">🔗 Link to Parent Account</label><select className="fi" value={f.parent_id||""} onChange={e=>up("parent_id",e.target.value)}><option value="">— No linked parent —</option>{parentUsers.map(u=><option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}</select><div style={{fontSize:".67rem",color:"var(--t2)",marginTop:"4px"}}>Parent will see this student's details after linking</div></div>}
    <div className="ma"><button className="btn bg2" onClick={onCancel}>Cancel</button><button className="btn bp" onClick={()=>onSave(f)} disabled={saving}>{saving?"Saving…":initial?"✓ Save Changes":"✓ Enrol Student 🎉"}</button></div>
  </>);
}

/* ══════ MARKS FORM ══════ */

/* ══════ MARKS HISTORY PAGE (Admin/Teacher) ══════ */
function MarksHistoryPage({students,setStudents,onAddMarks,addToast}){
  const[selected,setSelected]=useState(null);
  const[filter,setFilter]=useState("all");

  const deleteTest=async(testId,studentId)=>{
    if(!window.confirm("Delete this test result?"))return;
    try{
      await api.deleteTest(testId);
      setStudents(p=>p.map(s=>s.id===studentId?{...s,tests:(s.tests||[]).filter(t=>t.id!==testId)}:s));
      addToast("Test deleted");
    }catch(e){addToast(e.message,"err","⚠");}
  };

  const gradeColor=pct=>pct>=80?"var(--teal)":pct>=60?"var(--gold)":"var(--rose)";
  const gradeLetter=pct=>pct>=90?"A+":pct>=80?"A":pct>=70?"B+":pct>=60?"B":pct>=50?"C":"D";

  const filtStudents=filter==="all"?students:students.filter(s=>s.id===filter);

  return(<div className="fu">
    {/* Header */}
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"14px",flexWrap:"wrap",gap:"10px"}}>
      <div>
        <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:900,fontSize:"1rem",color:"var(--w)"}}>📝 Marks & Test History</div>
        <div style={{fontSize:".72rem",color:"var(--t2)",marginTop:"2px"}}>All tests · reflects instantly on parent portal</div>
      </div>
      <button className="btn bp" onClick={onAddMarks}>+ Add Marks</button>
    </div>

    {/* Student Filter */}
    <select className="fi" style={{marginBottom:"14px"}} value={filter} onChange={e=>setFilter(e.target.value)}>
      <option value="all">All Students ({students.length})</option>
      {students.map(s=><option key={s.id} value={s.id}>{s.name} ({s.grade}) — {s.tests?.length||0} tests</option>)}
    </select>

    {/* Student Cards with all tests */}
    {filtStudents.map((s,si)=>{
      const tests=s.tests||[];
      const avg=tests.length?Math.round(tests.reduce((a,t)=>a+(t.marks/t.total*100),0)/tests.length):null;
      const isOpen=selected===s.id;
      return(<div key={s.id} style={{background:"var(--card)",border:"1px solid var(--bdr)",borderRadius:"14px",marginBottom:"10px",overflow:"hidden"}}>
        {/* Student header - clickable to expand */}
        <div style={{padding:"14px 16px",display:"flex",alignItems:"center",gap:"12px",cursor:"pointer",background:isOpen?"rgba(245,168,0,.05)":"transparent"}} onClick={()=>setSelected(isOpen?null:s.id)}>
          <div className={`av ${getAv(si)}`}>{ini(s.name)}</div>
          <div style={{flex:1}}>
            <div style={{fontWeight:700,fontSize:".88rem",color:"var(--w)"}}>{s.name}</div>
            <div style={{fontSize:".7rem",color:"var(--t2)",marginTop:"1px"}}>{s.grade} · {s.subject}</div>
          </div>
          <div style={{textAlign:"right",marginRight:"8px"}}>
            <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:900,fontSize:"1.1rem",color:avg?gradeColor(avg):"var(--t3)"}}>{avg?`${avg}%`:"—"}</div>
            <div style={{fontSize:".65rem",color:"var(--t2)"}}>{tests.length} tests</div>
          </div>
          <span style={{color:"var(--t2)",fontSize:".8rem",transition:"transform .2s",transform:isOpen?"rotate(180deg)":"rotate(0deg)"}}>▼</span>
        </div>

        {/* All tests expanded */}
        {isOpen&&(<div style={{borderTop:"1px solid var(--bdr)"}}>
          {tests.length===0?(<div style={{padding:"20px",textAlign:"center",color:"var(--t2)",fontSize:".82rem"}}>No tests recorded yet</div>)
          :tests.map((t,ti)=>{
            const pct=Math.round(t.marks/t.total*100);
            const clr=gradeColor(pct);
            return(<div key={t.id||ti} style={{padding:"12px 16px",borderBottom:ti<tests.length-1?"1px solid rgba(255,255,255,.04)":"none",display:"flex",alignItems:"center",gap:"12px"}}>
              <div style={{width:"3px",height:"44px",borderRadius:"3px",background:clr,flexShrink:0}}/>
              <div style={{flex:1}}>
                <div style={{fontWeight:600,fontSize:".84rem",color:"var(--w)"}}>{t.name}</div>
                <div style={{fontSize:".68rem",color:"var(--t2)",marginTop:"2px",display:"flex",gap:"8px"}}>
                  <span>📅 {t.date}</span>
                  {t.subject&&<span>· {t.subject}</span>}
                </div>
                {t.remark&&<div style={{fontSize:".7rem",color:"var(--t2)",fontStyle:"italic",marginTop:"3px"}}>💬 {t.remark}</div>}
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:900,fontSize:"1.1rem",color:clr}}>{t.marks}<span style={{fontSize:".7rem",color:"var(--t2)",fontWeight:400}}>/{t.total}</span></div>
                <div style={{fontSize:".65rem",fontWeight:700,color:clr}}>Grade {gradeLetter(pct)}</div>
              </div>
              <button onClick={()=>deleteTest(t.id,s.id)} style={{background:"rgba(244,84,106,.1)",border:"1px solid rgba(244,84,106,.2)",borderRadius:"7px",color:"var(--rose)",padding:"4px 8px",cursor:"pointer",fontSize:".7rem",fontFamily:"'DM Sans',sans-serif",flexShrink:0}}>✕</button>
            </div>);
          })}
          {/* Summary bar */}
          {tests.length>0&&<div style={{padding:"10px 16px",background:"rgba(255,255,255,.02)",display:"flex",gap:"16px",fontSize:".72rem"}}>
            <span style={{color:"var(--teal)"}}>✓ Best: {Math.max(...tests.map(t=>Math.round(t.marks/t.total*100)))}%</span>
            <span style={{color:"var(--rose)"}}>✗ Lowest: {Math.min(...tests.map(t=>Math.round(t.marks/t.total*100)))}%</span>
            <span style={{color:"var(--gold)"}}>∅ Avg: {avg}%</span>
          </div>}
        </div>)}
      </div>);
    })}
  </div>);
}

function MarksForm({students,onCancel,onSave}){
  const[f,setF]=useState({student_id:"",name:"",marks:"",total:"100",subject:"",remark:"",date:todayStr()});
  const up=(k,v)=>setF(p=>({...p,[k]:v}));
  return(<>
    <div className="fg"><label className="fl">Student *</label><select className="fi" value={f.student_id} onChange={e=>{up("student_id",e.target.value);const s=students.find(x=>x.id===e.target.value);if(s)up("subject",s.subject);}}><option value="">— Select Student —</option>{students.map(s=><option key={s.id} value={s.id}>{s.name} ({s.grade})</option>)}</select></div>
    <div className="fr2"><div className="fg"><label className="fl">Test Name *</label><input className="fi" placeholder="Unit Test 1" value={f.name} onChange={e=>up("name",e.target.value)}/></div><div className="fg"><label className="fl">Subject</label><input className="fi" placeholder="Subject" value={f.subject} onChange={e=>up("subject",e.target.value)}/></div></div>
    <div className="fr2"><div className="fg"><label className="fl">Marks *</label><input className="fi" type="number" placeholder="85" value={f.marks} onChange={e=>up("marks",e.target.value)}/></div><div className="fg"><label className="fl">Out of *</label><input className="fi" type="number" placeholder="100" value={f.total} onChange={e=>up("total",e.target.value)}/></div></div>
    <div className="fg"><label className="fl">Date</label><input className="fi" value={f.date} onChange={e=>up("date",e.target.value)}/></div>
    <div className="fg"><label className="fl">Remark</label><textarea className="fi" rows={2} placeholder="Teacher's comment…" value={f.remark} onChange={e=>up("remark",e.target.value)}/></div>
    {f.marks&&f.total&&<div style={{background:"var(--gf)",borderRadius:"10px",padding:"10px 14px",marginBottom:"12px",textAlign:"center"}}>Score: <strong style={{color:"var(--gold)",fontFamily:"'Nunito',sans-serif",fontSize:"1.1rem"}}>{Math.round(f.marks/f.total*100)}%</strong></div>}
    <div className="ma"><button className="btn bg2" onClick={onCancel}>Cancel</button><button className="btn bp" onClick={()=>onSave({...f,marks:parseInt(f.marks),total:parseInt(f.total)})}>💾 Save Marks</button></div>
  </>);
}

/* ══════ ENQUIRIES PANEL (Admin) ══════ */
function EnquiriesPanel({enquiries,setEnquiries,addToast,feeStruct}){
  const[filter,setFilter]=useState("all");
  const[editEnq,setEditEnq]=useState(null);
  const[notes,setNotes]=useState("");
  const filtEnq=filter==="all"?enquiries:enquiries.filter(e=>e.status===filter);

  const updateStatus=async(id,status)=>{
    try{const upd=await api.updateEnquiry(id,{status});setEnquiries(p=>p.map(e=>e.id===id?upd:e));addToast(`Enquiry marked ${status}`);}
    catch(e){addToast(e.message,"err","⚠");}
  };
  const saveNotes=async()=>{
    if(!editEnq)return;
    try{const upd=await api.updateEnquiry(editEnq.id,{notes});setEnquiries(p=>p.map(e=>e.id===editEnq.id?upd:e));setEditEnq(null);addToast("Notes saved ✓");}
    catch(e){addToast(e.message,"err","⚠");}
  };
  const deleteEnq=async(id)=>{
    try{await api.deleteEnquiry(id);setEnquiries(p=>p.filter(e=>e.id!==id));addToast("Enquiry deleted");}
    catch(e){addToast(e.message,"err","⚠");}
  };

  const statusBadge=(s)=>({new:"enq-new",contacted:"enq-contacted",enrolled:"enq-enrolled",closed:"enq-closed"}[s]||"bo");

  return(<>
    <div className="sg sg4" style={{marginBottom:"16px"}}>
      {[{cl:"c3",ic:"📋",lb:"Total",val:enquiries.length},{cl:"c4",ic:"🆕",lb:"New",val:enquiries.filter(e=>e.status==="new").length},{cl:"c1",ic:"📞",lb:"Contacted",val:enquiries.filter(e=>e.status==="contacted").length},{cl:"c2",ic:"✅",lb:"Enrolled",val:enquiries.filter(e=>e.status==="enrolled").length}].map((s,i)=>(<div key={i} className={`sc ${s.cl} fu`}><div className="si">{s.ic}</div><div className="slbl">{s.lb}</div><div className="sval" style={{fontSize:"1.5rem"}}>{s.val}</div></div>))}
    </div>
    <div className="tabs">{["all","new","contacted","enrolled","closed"].map(t=><button key={t} className={`tab${filter===t?" active":""}`} onClick={()=>setFilter(t)}>{t}</button>)}</div>
    <div className="card fu">
      <div className="ch"><span className="ct">{filtEnq.length} Enquiries</span></div>
      <div style={{padding:"0 18px"}}><div className="tbl-wrap"><table className="tbl">
        <thead><tr><th>Name</th><th>Phone</th><th>Class</th><th>Subject</th><th>Fee/mo</th><th>Date</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>{filtEnq.map((e,i)=>{
          const fee=e.grade&&e.subject?feeStruct[e.grade]?.[e.subject]:null;
          return(<tr key={e.id}>
            <td><div style={{fontWeight:600}}>{e.name}</div>{e.email&&<div style={{fontSize:".7rem",color:"var(--t2)"}}>{e.email}</div>}</td>
            <td><a href={`tel:${e.phone}`} style={{color:"var(--teal)",textDecoration:"none",fontWeight:600}}>{e.phone}</a></td>
            <td><span className="chip">{e.grade||"—"}</span></td>
            <td style={{fontSize:".76rem",color:"var(--t2)"}}>{e.subject||"—"}</td>
            <td style={{fontWeight:600,color:"var(--gold)"}}>{fee?`₹${fee.toLocaleString()}`:"—"}</td>
            <td style={{fontSize:".72rem",color:"var(--t2)"}}>{new Date(e.created_at).toLocaleDateString("en-IN",{day:"2-digit",month:"short"})}</td>
            <td><span className={`bd ${statusBadge(e.status)}`}>{e.status}</span></td>
            <td><div style={{display:"flex",gap:"3px",flexWrap:"wrap"}}>
              {e.status==="new"&&<button className="btn bt" style={{padding:"2px 7px",fontSize:".65rem"}} onClick={()=>updateStatus(e.id,"contacted")}>📞 Called</button>}
              {e.status==="contacted"&&<button className="btn bgr" style={{padding:"2px 7px",fontSize:".65rem"}} onClick={()=>updateStatus(e.id,"enrolled")}>✓ Enrolled</button>}
              <button className="btn bg2" style={{padding:"2px 7px",fontSize:".65rem"}} onClick={()=>{setEditEnq(e);setNotes(e.notes||"");}}>📝</button>
              <button className="btn br2" style={{padding:"2px 7px",fontSize:".65rem"}} onClick={()=>deleteEnq(e.id)}>🗑️</button>
            </div></td>
          </tr>);
        })}</tbody>
      </table></div></div>
    </div>
    {editEnq&&<div className="ov"><div className="modal"><div className="mh"><h3>📝 Notes — {editEnq.name}</h3><button className="cls" onClick={()=>setEditEnq(null)}>✕</button></div>
      <div style={{marginBottom:"14px",padding:"12px",background:"rgba(255,255,255,.04)",borderRadius:"10px",fontSize:".82rem"}}>
        <div><strong style={{color:"var(--w)"}}>{editEnq.name}</strong> · {editEnq.phone}</div>
        {editEnq.email&&<div style={{color:"var(--t2)"}}>{editEnq.email}</div>}
        {editEnq.grade&&<div style={{color:"var(--t2)"}}>{editEnq.grade} · {editEnq.subject}</div>}
        {editEnq.message&&<div style={{marginTop:"8px",color:"var(--t1)",fontStyle:"italic"}}>"{editEnq.message}"</div>}
      </div>
      <div className="fg"><label className="fl">Admin Notes</label><textarea className="fi" rows={4} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Follow-up notes, call details…"/></div>
      <div style={{marginBottom:"14px"}}><label className="fl">Update Status</label><div style={{display:"flex",gap:"6px"}}>
        {["new","contacted","enrolled","closed"].map(s=><button key={s} className={`btn ${editEnq.status===s?"bp":"bg2"}`} style={{padding:"4px 10px",fontSize:".72rem"}} onClick={()=>{setEditEnq(p=>({...p,status:s}));api.updateEnquiry(editEnq.id,{status:s}).then(upd=>setEnquiries(p=>p.map(e=>e.id===upd.id?upd:e)));}}>{s}</button>)}
      </div></div>
      <div className="ma"><button className="btn bg2" onClick={()=>setEditEnq(null)}>Cancel</button><button className="btn bp" onClick={saveNotes}>💾 Save Notes</button></div>
    </div></div>}
  </>);
}

/* ══════ ADMIN CMS ══════ */
function AdminCMS({homeData,setHomeData,notices,setNotices,feeStruct,setFeeStruct,allUsers,setAllUsers,students,setStudents,addToast}){
  const[tab,setTab]=useState("users");
  const[nTag,setNTag]=useState("General");
  const[nTxt,setNTxt]=useState("");
  const[imgUrl,setImgUrl]=useState("");
  const[imgCap,setImgCap]=useState("");
  const[saving,setSaving]=useState(false);
  const[imgUploading,setImgUploading]=useState(false);
  const[imgPreview,setImgPreview]=useState("");
  const[uploadMode,setUploadMode]=useState("device");
  const pending=allUsers.filter(u=>!u.approved&&u.role!=="admin");

  const addNotice=async()=>{if(!nTxt.trim())return;try{const n=await api.addNotice({tag:nTag,text:nTxt});setNotices(p=>[n,...p]);setNTxt("");addToast("Notice posted! 📢");}catch(e){addToast(e.message,"err","⚠");}};
  const deleteNotice=async(id)=>{try{await api.deleteNotice(id);setNotices(p=>p.filter(n=>n.id!==id));addToast("Notice deleted");}catch(e){addToast(e.message,"err","⚠");}};
  const addImg=async()=>{
    if(!imgUrl)return;
    setImgUploading(true);
    try{
      const img=await api.addGalleryImage({url:imgUrl,caption:imgCap||"Photo"});
      setHomeData(h=>({...h,images:[...(h.images||[]),img]}));
      setImgUrl("");setImgCap("");setImgPreview("");
      addToast("Photo uploaded! ✓","mark","✓");
    }catch(e){addToast(e.message||"Upload failed","err","⚠");}
    finally{setImgUploading(false);}
  };
  const handleFileUpload=async(e)=>{
    const file=e.target.files[0];
    if(!file)return;
    if(file.size>10*1024*1024)return addToast("File too large. Max 10MB","err","⚠");
    setImgUploading(true);
    try{
      // Compress first
      const b64=await compressToBase64(file);
      setImgPreview(b64);
      // Save compressed image directly
      setImgUrl(b64);
      addToast("Image ready! Add caption and click Upload ✓","mark","✓");
    }catch(err){
      addToast("Upload failed. Try a smaller image or use URL","err","⚠");
    }
    finally{setImgUploading(false);}
  };
  const compressToBase64=(file)=>new Promise((resolve,reject)=>{
    const reader=new FileReader();
    reader.onload=ev=>{
      const img=new Image();
      img.onload=()=>{
        // Auto-size: max 800px wide, compress until under 200KB
        const MAX_W=800, MAX_H=800;
        let w=img.width, h=img.height;
        if(w>MAX_W){h=Math.round(h*MAX_W/w);w=MAX_W;}
        if(h>MAX_H){w=Math.round(w*MAX_H/h);h=MAX_H;}
        const canvas=document.createElement("canvas");
        canvas.width=w; canvas.height=h;
        canvas.getContext("2d").drawImage(img,0,0,w,h);
        // Try reducing quality until under 200KB
        let quality=0.8;
        let result=canvas.toDataURL("image/jpeg",quality);
        while(result.length>200*1024 && quality>0.3){
          quality-=0.1;
          result=canvas.toDataURL("image/jpeg",quality);
        }
        resolve(result);
      };
      img.onerror=reject;
      img.src=ev.target.result;
    };
    reader.onerror=reject;
    reader.readAsDataURL(file);
  });
  const delImg=async(id)=>{try{await api.deleteGalleryImage(id);setHomeData(h=>({...h,images:(h.images||[]).filter(x=>x.id!==id)}));addToast("Image removed");}catch(e){addToast(e.message,"err","⚠");}};
  const saveHome=async()=>{
    setSaving(true);
    try{
      await api.updateHome({
        heroTitle:homeData.heroTitle,heroSubtitle:homeData.heroSubtitle,
        heroDesc:homeData.heroDesc,phone:homeData.phone,timings:homeData.timings,
        mapAddress:homeData.mapAddress,features:homeData.features,
        schedule:homeData.schedule||[],lat:homeData.lat,lng:homeData.lng
      });
      addToast("Saved! ✓");
    }catch(e){addToast(e.message,"err","⚠");}
    finally{setSaving(false);}
  };
  const saveFees=async()=>{setSaving(true);try{const upd=await api.updateFeeStructure(feeStruct);setFeeStruct(upd);addToast("Fee structure published! 💰");}catch(e){addToast(e.message,"err","⚠");}finally{setSaving(false);}};
  const approveUser=async(id)=>{try{await api.approveUser(id);setAllUsers(p=>p.map(u=>u.id===id?{...u,approved:true}:u));addToast("User approved! ✓");}catch(e){addToast(e.message,"err","⚠");}};
  const rejectUser=async(id)=>{try{await api.deleteUser(id);setAllUsers(p=>p.filter(u=>u.id!==id));addToast("User removed.");}catch(e){addToast(e.message,"err","⚠");}};
  const linkParentToStudent=async(studentId,parentId)=>{try{const s=await api.linkParent(studentId,parentId);setStudents(p=>p.map(x=>x.id===s.id?s:x));addToast("Parent linked! ✓");}catch(e){addToast(e.message,"err","⚠");}};
  const uptFeat=(i,k,v)=>setHomeData(h=>{const f=[...h.features];f[i]={...f[i],[k]:v};return{...h,features:f};});

  const parentUsers=allUsers.filter(u=>u.role==="parent"&&u.approved);

  return(<>
    <div className="tabs">{[{id:"users",lbl:`👥 Users${pending.length>0?` (${pending.length})`:""}`},{id:"link",lbl:"🔗 Parent Links"},{id:"notices",lbl:"📢 Notices"},{id:"home_ed",lbl:"🏠 Home"},{id:"gallery",lbl:"📸 Gallery"},{id:"map",lbl:"📍 Map"},{id:"fees_cms",lbl:"💰 Fees"}].map(t=><button key={t.id} className={`tab${tab===t.id?" active":""}`} onClick={()=>setTab(t.id)}>{t.lbl}</button>)}</div>

    {/* USERS — APPROVALS */}
    {tab==="users"&&<>
      {pending.length>0&&<div className="card" style={{marginBottom:"16px",border:"1px solid rgba(245,168,0,.3)"}}>
        <div className="ch" style={{background:"rgba(245,168,0,.06)"}}><span className="ct">⏳ Pending Approvals ({pending.length})</span></div>
        <div style={{padding:"0 18px"}}><div className="tbl-wrap"><table className="tbl"><thead><tr><th>Name</th><th>Role</th><th>Email</th><th>Phone</th><th>Action</th></tr></thead>
          <tbody>{pending.map(u=>(<tr key={u.id} className="pending-row"><td style={{fontWeight:600}}>{u.name}</td><td><span className={`bd ${u.role==="teacher"?"bb":"bg3"}`}>{u.role}</span></td><td style={{fontSize:".78rem",color:"var(--t2)"}}>{u.email}</td><td style={{fontSize:".78rem",color:"var(--t2)"}}>{u.phone||"—"}</td><td><button className="approve-btn" onClick={()=>approveUser(u.id)}>✓ Approve</button><button className="reject-btn" onClick={()=>rejectUser(u.id)}>✕ Reject</button></td></tr>))}</tbody>
        </table></div></div>
      </div>}
      <div className="card"><div className="ch"><span className="ct">All Users ({allUsers.length})</span></div>
        <div style={{padding:"0 18px"}}><div className="tbl-wrap"><table className="tbl"><thead><tr><th>Name</th><th>Role</th><th>Email</th><th>Status</th><th>Action</th></tr></thead>
          <tbody>{allUsers.map((u,i)=>(<tr key={u.id}><td><div className="nc"><div className={`av ${getAv(i)}`}>{ini(u.name)}</div>{u.name}</div></td><td><span className={`bd ${u.role==="admin"?"bo":u.role==="teacher"?"bb":"bg3"}`}>{u.role}</span></td><td style={{fontSize:".78rem",color:"var(--t2)"}}>{u.email}</td><td><span className={`bd ${u.approved?"bgr2":"br3"}`}>{u.approved?"Active":"Pending"}</span></td><td>{u.role!=="admin"&&<button className="reject-btn" onClick={()=>rejectUser(u.id)}>Remove</button>}</td></tr>))}</tbody>
        </table></div></div>
      </div>
    </>}

    {/* PARENT–STUDENT LINKING */}
    {tab==="link"&&<>
      <div style={{background:"rgba(79,142,247,.07)",border:"1px solid rgba(79,142,247,.18)",borderRadius:"10px",padding:"12px 14px",marginBottom:"14px",fontSize:".78rem",color:"var(--t2)"}}>
        🔗 Link a parent's login account to their child so the parent can view their child's marks, attendance and fees.
      </div>
      <div className="card"><div className="ch"><span className="ct">Student → Parent Linking</span></div>
        <div style={{padding:"0 18px"}}><div className="tbl-wrap"><table className="tbl"><thead><tr><th>Student</th><th>Grade</th><th>Current Parent</th><th>Link Parent Account</th></tr></thead>
          <tbody>{students.map((s,i)=>(<tr key={s.id}>
            <td><div className="nc"><div className={`av ${getAv(i)}`}>{ini(s.name)}</div>{s.name}</div></td>
            <td><span className="chip">{s.grade}</span></td>
            <td style={{fontSize:".76rem",color:"var(--t2)"}}>{s.parentUser?`${s.parentUser.name} (${s.parentUser.email})`:s.parent_name||"—"}</td>
            <td><select className="fi" style={{padding:"5px 9px",fontSize:".76rem",width:"auto",minWidth:"180px"}} value={s.parent_id||""} onChange={e=>linkParentToStudent(s.id,e.target.value||null)}>
              <option value="">— No parent linked —</option>
              {parentUsers.map(p=><option key={p.id} value={p.id}>{p.name} ({p.email})</option>)}
            </select></td>
          </tr>))}</tbody>
        </table></div></div>
      </div>
    </>}

    {/* NOTICES */}
    {tab==="notices"&&<>
      <div className="cms-section">
        <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:800,fontSize:".95rem",color:"var(--w)",marginBottom:"14px"}}>📢 Post Notice</div>
        <div className="fr2" style={{marginBottom:"12px"}}><div className="fg"><label className="fl">Type</label><select className="fi" value={nTag} onChange={e=>setNTag(e.target.value)}>{NOTICE_TAGS.map(t=><option key={t}>{t}</option>)}</select></div></div>
        <div className="fg"><label className="fl">Message *</label><textarea className="fi" rows={3} value={nTxt} onChange={e=>setNTxt(e.target.value)} placeholder="Exam alert, holiday, leave update…"/></div>
        <button className="btn bp" onClick={addNotice}>📢 Post Notice</button>
      </div>
      <div className="card"><div className="ch"><span className="ct">All Notices</span></div>
        <div className="cb" style={{padding:"6px 18px"}}>{notices.map((n,i)=>{const tc=tagColor[n.tag]||"#7b9ccc";return(<div key={n.id||i} style={{display:"flex",alignItems:"flex-start",gap:"12px",padding:"11px 0",borderBottom:"1px solid rgba(255,255,255,.05)"}}>
          <div style={{flex:1}}><div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"4px"}}><span style={{fontSize:".62rem",fontWeight:800,textTransform:"uppercase",color:tc,background:tc+"18",padding:"2px 8px",borderRadius:"20px"}}>{n.tag}</span><span style={{fontSize:".62rem",color:"var(--t2)"}}>{n.date}</span></div><div style={{fontSize:".83rem",color:"var(--t1)"}}>{n.text}</div></div>
          <button className="btn br2" style={{padding:"3px 9px",fontSize:".7rem",flexShrink:0}} onClick={()=>deleteNotice(n.id)}>Delete</button>
        </div>);})}
        </div>
      </div>
    </>}

    {/* HOME EDITOR */}
    {tab==="home_ed"&&<div className="cms-section">
      <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:800,fontSize:".95rem",color:"var(--w)",marginBottom:"14px"}}>🏠 Edit Home Page</div>
      <div className="fg"><label className="fl">Hero Title</label><input className="fi" value={homeData.heroTitle||""} onChange={e=>setHomeData(h=>({...h,heroTitle:e.target.value}))}/></div>
      <div className="fg"><label className="fl">Hero Subtitle</label><input className="fi" value={homeData.heroSubtitle||""} onChange={e=>setHomeData(h=>({...h,heroSubtitle:e.target.value}))}/></div>
      <div className="fg"><label className="fl">Description</label><textarea className="fi" rows={3} value={homeData.heroDesc||""} onChange={e=>setHomeData(h=>({...h,heroDesc:e.target.value}))}/></div>
      <div className="fr2"><div className="fg"><label className="fl">Phone</label><input className="fi" value={homeData.phone||""} onChange={e=>setHomeData(h=>({...h,phone:e.target.value}))}/></div><div className="fg"><label className="fl">Timings</label><input className="fi" value={homeData.timings||""} onChange={e=>setHomeData(h=>({...h,timings:e.target.value}))}/></div></div>
      <div style={{fontWeight:700,color:"var(--gold)",fontSize:".76rem",marginBottom:"10px",textTransform:"uppercase"}}>Feature Cards</div>
      {homeData.features?.map((f,i)=>(<div key={i} className="fr2" style={{marginBottom:"8px"}}><div style={{display:"flex",gap:"8px"}}><input className="fi" placeholder="Icon" value={f.icon} onChange={e=>uptFeat(i,"icon",e.target.value)} style={{width:"56px"}}/><input className="fi" placeholder="Title" value={f.title} onChange={e=>uptFeat(i,"title",e.target.value)}/></div><input className="fi" placeholder="Description" value={f.desc} onChange={e=>uptFeat(i,"desc",e.target.value)}/></div>))}
      <button className="btn bp" style={{marginTop:"12px"}} onClick={saveHome} disabled={saving}>{saving?"Saving…":"💾 Save Changes"}</button>
    </div>}

    {/* GALLERY */}
    {tab==="gallery"&&<>
      <div className="cms-section">
        <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:800,fontSize:".95rem",color:"var(--w)",marginBottom:"14px"}}>📸 Gallery</div>

        {/* Upload Mode Toggle */}
        <div style={{display:"flex",background:"rgba(255,255,255,.04)",borderRadius:"10px",padding:"3px",marginBottom:"16px",width:"fit-content"}}>
          <button onClick={()=>{setUploadMode("device");setImgUrl("");setImgPreview("");}} style={{padding:"6px 16px",borderRadius:"8px",border:"none",fontFamily:"'DM Sans',sans-serif",fontSize:".78rem",fontWeight:600,cursor:"pointer",background:uploadMode==="device"?"var(--card)":"transparent",color:uploadMode==="device"?"var(--gold)":"var(--t2)",transition:"all .18s"}}>📱 From Device</button>
          <button onClick={()=>{setUploadMode("url");setImgUrl("");setImgPreview("");}} style={{padding:"6px 16px",borderRadius:"8px",border:"none",fontFamily:"'DM Sans',sans-serif",fontSize:".78rem",fontWeight:600,cursor:"pointer",background:uploadMode==="url"?"var(--card)":"transparent",color:uploadMode==="url"?"var(--gold)":"var(--t2)",transition:"all .18s"}}>🔗 From URL</button>
        </div>

        {/* Device Upload */}
        {uploadMode==="device"&&<>
          {/* Preview after selection */}
          {imgPreview&&<div style={{marginBottom:"12px",borderRadius:"12px",overflow:"hidden",border:"2px solid rgba(34,211,200,.3)",position:"relative"}}>
            <img src={imgPreview} alt="preview" style={{width:"100%",maxHeight:"200px",objectFit:"cover",display:"block"}}/>
            <div style={{position:"absolute",bottom:0,left:0,right:0,background:"linear-gradient(transparent,rgba(0,0,0,.7))",padding:"8px 12px",fontSize:".72rem",color:"var(--teal)",fontWeight:600}}>✓ Image ready — add caption below and click Upload</div>
            <button onClick={()=>{setImgPreview("");setImgUrl("");}} style={{position:"absolute",top:"8px",right:"8px",background:"rgba(244,84,106,.8)",border:"none",borderRadius:"6px",color:"#fff",width:"26px",height:"26px",cursor:"pointer",fontSize:".8rem"}}>✕</button>
          </div>}

          {/* Upload buttons - 2 separate buttons for Camera and Gallery */}
          {!imgPreview&&<div style={{marginBottom:"12px"}}>
            {imgUploading?(<div style={{textAlign:"center",padding:"30px",border:"2px dashed rgba(245,168,0,.3)",borderRadius:"14px"}}>
              <div className="spin" style={{width:"32px",height:"32px",border:"3px solid rgba(255,255,255,.1)",borderTopColor:"var(--gold)",borderRadius:"50%",margin:"0 auto 10px"}}/>
              <div style={{color:"var(--t2)",fontSize:".8rem"}}>Uploading image…</div>
            </div>):(<>
              {/* Camera button - opens camera directly on mobile */}
              <label style={{display:"block",cursor:"pointer",marginBottom:"10px"}}>
                <div style={{border:"2px solid rgba(245,168,0,.3)",borderRadius:"12px",padding:"16px",textAlign:"center",background:"rgba(245,168,0,.05)",display:"flex",alignItems:"center",justifyContent:"center",gap:"10px"}}>
                  <span style={{fontSize:"1.4rem"}}>📷</span>
                  <div><div style={{fontWeight:700,color:"var(--w)",fontSize:".85rem"}}>Take Photo</div><div style={{fontSize:".67rem",color:"var(--t2)"}}>Open camera directly</div></div>
                </div>
                <input type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={handleFileUpload} disabled={imgUploading}/>
              </label>
              {/* Gallery button - opens photo library */}
              <label style={{display:"block",cursor:"pointer"}}>
                <div style={{border:"2px solid rgba(79,142,247,.3)",borderRadius:"12px",padding:"16px",textAlign:"center",background:"rgba(79,142,247,.05)",display:"flex",alignItems:"center",justifyContent:"center",gap:"10px"}}>
                  <span style={{fontSize:"1.4rem"}}>🖼️</span>
                  <div><div style={{fontWeight:700,color:"var(--w)",fontSize:".85rem"}}>Choose from Gallery</div><div style={{fontSize:".67rem",color:"var(--t2)"}}>JPG, PNG, WEBP · max 10MB</div></div>
                </div>
                <input type="file" accept="image/*" style={{display:"none"}} onChange={handleFileUpload} disabled={imgUploading}/>
              </label>
            </>)}
          </div>}
        </>}

        {/* URL Upload */}
        {uploadMode==="url"&&<div className="fg" style={{marginBottom:"12px"}}>
          <label className="fl">Image URL</label>
          <input className="fi" placeholder="https://example.com/photo.jpg" value={imgUrl} onChange={e=>setImgUrl(e.target.value)}/>
        </div>}

        {/* Caption + Upload button */}
        <div className="fr2" style={{marginBottom:"0"}}>
          <div className="fg"><label className="fl">Caption</label><input className="fi" placeholder="e.g. Our Classroom" value={imgCap} onChange={e=>setImgCap(e.target.value)}/></div>
          <div className="fg" style={{display:"flex",alignItems:"flex-end"}}><button className="btn bp" style={{width:"100%",justifyContent:"center"}} onClick={addImg} disabled={!imgUrl||imgUploading}>{imgUploading?"Processing…":"📤 Upload"}</button></div>
        </div>
      </div>

      {/* Gallery Grid */}
      <div className="card">
        <div className="ch"><span className="ct">Gallery ({homeData.images?.length||0} photos)</span></div>
        <div className="cb">
          <div className="gallery">
            {homeData.images?.map((img,i)=>(<div key={img.id||i} className="gimg">
              <img src={img.url} alt={img.caption} onError={e=>{e.target.src="https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=400&q=60";}}/>
              <div className="gcap">{img.caption}</div>
              <button className="gdel" onClick={()=>delImg(img.id)}>✕</button>
            </div>))}
            {(!homeData.images?.length)&&<div style={{gridColumn:"1/-1",padding:"40px",textAlign:"center",color:"var(--t2)",fontSize:".82rem"}}>
              <div style={{fontSize:"2rem",marginBottom:"8px"}}>📷</div>
              No images yet. Upload your first photo!
            </div>}
          </div>
        </div>
      </div>
    </>}

    {/* MAP */}
    {tab==="map"&&<>
      <div className="cms-section">
        <div style={{fontFamily:"'Nunito',sans-serif",fontWeight:800,fontSize:".95rem",color:"var(--w)",marginBottom:"14px"}}>📍 Map Location</div>
        <div className="fg"><label className="fl">Full Address</label><input className="fi" value={homeData.mapAddress||""} onChange={e=>setHomeData(h=>({...h,mapAddress:e.target.value}))}/></div>
        <div className="fr2" style={{marginBottom:"12px"}}>
          <div className="fg"><label className="fl">Latitude (optional)</label><input className="fi" type="number" step="0.0001" placeholder="e.g. 12.9716" value={homeData.lat||""} onChange={e=>setHomeData(h=>({...h,lat:e.target.value}))}/></div>
          <div className="fg"><label className="fl">Longitude (optional)</label><input className="fi" type="number" step="0.0001" placeholder="e.g. 77.5946" value={homeData.lng||""} onChange={e=>setHomeData(h=>({...h,lng:e.target.value}))}/></div>
        </div>
        <div style={{fontSize:".72rem",color:"var(--t2)",marginBottom:"12px",background:"rgba(79,142,247,.07)",border:"1px solid rgba(79,142,247,.18)",borderRadius:"9px",padding:"9px 12px"}}>
          💡 To get exact coordinates: Open Google Maps → right-click your location → click the coordinates shown. Paste lat & lng above for precise pin.
        </div>
        <div style={{display:"flex",gap:"8px",flexWrap:"wrap",marginBottom:"12px"}}>
          <a href="https://maps.google.com" target="_blank" rel="noreferrer" className="btn bg2" style={{fontSize:".76rem",padding:"6px 12px",textDecoration:"none"}}>🗺️ Open Google Maps</a>
          <button className="btn bp" onClick={saveHome} disabled={saving}>{saving?"Saving…":"💾 Save Location"}</button>
        </div>
      </div>
      <div className="map-card"><div className="ch"><span className="ct">Map Preview</span></div>
        <iframe className="map-frame" title="Map"
          src={homeData.lat&&homeData.lng
            ? `https://maps.google.com/maps?q=${homeData.lat},${homeData.lng}&z=17&output=embed`
            : `https://maps.google.com/maps?q=${encodeURIComponent(homeData.mapAddress||"Nagharbhavi Bangalore")}&z=16&output=embed`}
          allowFullScreen loading="lazy"/>
      </div>
    </>}

    {/* FEES EDITOR */}
    {tab==="fees_cms"&&(()=>{
      // Get all subjects currently in feeStruct + ALL_SUBJECTS combined
      const allSubjectsInStruct=new Set(ALL_SUBJECTS);
      Object.values(feeStruct).forEach(subs=>Object.keys(subs).forEach(s=>allSubjectsInStruct.add(s)));
      const subjectCols=[...allSubjectsInStruct];
      return(<div className="card"><div className="ch"><span className="ct">💰 Fee Structure</span><span className="bd bgr2"><span className="live-dot" style={{marginRight:"4px"}}></span>Live</span></div>
        <div className="cb">
          <div style={{fontSize:".76rem",color:"var(--t2)",marginBottom:"12px"}}>✏️ All fields are editable. Enter ₹0 or leave blank to remove. Changes save when you click Publish.</div>

          <div style={{fontSize:".72rem",color:"var(--gold)",marginBottom:"12px",display:"flex",alignItems:"center",gap:"6px"}}>
            <span>✏️</span><span>Click any field to edit. Gold = has value. Click Save to publish.</span>
          </div>

          <div className="tbl-wrap">
            <table className="tbl">
              <thead><tr>
                <th style={{position:"sticky",left:0,background:"var(--card)",zIndex:2,minWidth:"80px"}}>Grade</th>
                {subjectCols.map(s=><th key={s} style={{minWidth:"100px",fontSize:".65rem"}}>{s}</th>)}
              </tr></thead>
              <tbody>{GRADES.map(g=>(<tr key={g}>
                <td style={{fontWeight:800,color:"var(--gold)",position:"sticky",left:0,background:"var(--card)",zIndex:1}}>{g}</td>
                {subjectCols.map(s=>(
                  <td key={s}>
                    <div style={{display:"flex",alignItems:"center",gap:"2px"}}>
                      <span style={{fontSize:".7rem",color:"var(--gold)"}}>₹</span>
                      <input
                        type="number"
                        placeholder="0"
                        style={{width:"82px",padding:"6px 8px",background:feeStruct[g]?.[s]?"rgba(245,168,0,.08)":"rgba(255,255,255,.04)",border:feeStruct[g]?.[s]?"1px solid rgba(245,168,0,.3)":"1px solid var(--bdr)",borderRadius:"7px",color:feeStruct[g]?.[s]?"var(--gold)":"var(--t2)",fontFamily:"'DM Sans',sans-serif",fontSize:".8rem",outline:"none",fontWeight:feeStruct[g]?.[s]?700:400}}
                        value={feeStruct[g]?.[s]||""}
                        onChange={e=>{
                          const v=parseInt(e.target.value)||0;
                          setFeeStruct(p=>{
                            const n={...p};
                            if(!n[g])n[g]={};
                            if(v>0)n[g][s]=v;
                            else delete n[g][s];
                            return{...n};
                          });
                        }}
                      />
                    </div>
                  </td>
                ))}
              </tr>))}</tbody>
            </table>
          </div>
          <div style={{display:"flex",gap:"10px",marginTop:"14px",flexWrap:"wrap"}}>
            <button className="btn bp" style={{flex:1,justifyContent:"center"}} onClick={saveFees} disabled={saving}>{saving?"Saving…":"💾 Save & Publish All Fees"}</button>
          </div>
        </div>
      </div>);
    })()}
  </>);
}

/* ══════ ROOT ══════ */

/* ══════ PWA INSTALL PROMPT ══════ */
function InstallPrompt(){
  const[prompt,setPrompt]=useState(null);
  const[show,setShow]=useState(false);
  const[dismissed,setDismissed]=useState(()=>localStorage.getItem("pwa_dismissed")==="1");

  useEffect(()=>{
    const handler=e=>{e.preventDefault();setPrompt(e);if(!dismissed)setShow(true);};
    window.addEventListener("beforeinstallprompt",handler);
    return()=>window.removeEventListener("beforeinstallprompt",handler);
  },[dismissed]);

  const install=async()=>{
    if(!prompt)return;
    prompt.prompt();
    const{outcome}=await prompt.userChoice;
    if(outcome==="accepted"){setShow(false);setPrompt(null);}
  };
  const dismiss=()=>{setShow(false);setDismissed(true);localStorage.setItem("pwa_dismissed","1");};

  if(!show||dismissed)return null;
  return(<div className="install-banner">
    <div className="ib-icon"><img src="/logo192.png" alt="V Learn"/></div>
    <div className="ib-text">
      <div className="ib-title">Install V Learn App</div>
      <div className="ib-sub">Add to home screen for quick access</div>
    </div>
    <button className="ib-btn" onClick={install}>Install</button>
    <button className="ib-close" onClick={dismiss}>✕</button>
  </div>);
}

export default function App(){
  const[screen,setScreen]=useState("loading");
  const[user,setUser]=useState(null);
  const[students,setStudents]=useState([]);
  const[notices,setNotices]=useState([]);
  const[feeStruct,setFeeStruct]=useState({});
  const[homeData,setHomeData]=useState({});
  const[allUsers,setAllUsers]=useState([]);
  const[enquiries,setEnquiries]=useState([]);
  const[loadErr,setLoadErr]=useState("");
  const{toasts,addToast}=useToasts();

  const loadData=useCallback(async(u)=>{
    try{
      const[studs,nots,fees,home]=await Promise.all([api.getStudents(),api.getNotices(),api.getFeeStructure(),api.getHome()]);
      setStudents(studs);setNotices(nots);setFeeStruct(fees);setHomeData(home);
      if(u?.role==="admin"||u?.role==="teacher"){
        const users=await api.getUsers();setAllUsers(users);
      }
      if(u?.role==="admin"){const enqs=await api.getEnquiries();setEnquiries(enqs);}
      setLoadErr("");
    }catch(e){setLoadErr("Cannot connect to server. Is the backend running on port 4000?");}
  },[]);

  useEffect(()=>{
    const token=localStorage.getItem("vlearn_token");
    const savedUser=localStorage.getItem("vlearn_user");
    if(token&&savedUser){
      try{const u=JSON.parse(savedUser);setUser(u);loadData(u).then(()=>setScreen("app"));}
      catch{localStorage.clear();setScreen("auth");}
    }else{
      api.getFeeStructure().then(setFeeStruct).catch(()=>{});
      api.getNotices().then(setNotices).catch(()=>{});
      setScreen("auth");
    }
  },[loadData]);

  useEffect(()=>{
    if(!user)return;
    const disc=connectSocket(user.id,user.role,{
      student_added:(s)=>{setStudents(p=>p.find(x=>x.id===s.id)?p:[...p,s]);addToast(`New student: ${s.name}`,"msg","👨‍🎓");},
      student_updated:(s)=>setStudents(p=>p.map(x=>x.id===s.id?s:x)),
      student_deleted:({id})=>setStudents(p=>p.filter(s=>s.id!==id)),
      attendance_marked:({markedBy,date})=>{api.getStudents().then(setStudents);if(user.role==="parent")addToast(`Attendance marked for ${date}`,"mark","📅");else if(markedBy!==user.name)addToast(`${markedBy} marked attendance`,"msg","📅");},
      marks_added:({test,student})=>{setStudents(p=>p.map(s=>s.id===student?.id?{...s,tests:[test,...(s.tests||[])]}:s));if(user.role==="parent")addToast(`New marks: ${test.marks}/${test.total}`,"mark","📊");},
      marks_deleted:({id})=>setStudents(p=>p.map(s=>({...s,tests:(s.tests||[]).filter(t=>t.id!==id)}))),
      fee_updated:({student_id,fee_status,feeStatus,name})=>{const fs=feeStatus||fee_status;setStudents(p=>p.map(s=>s.id===student_id?{...s,feeStatus:fs,fee_status:fs}:s));if(user.role==="admin")addToast(`${name} fee → ${fs}`,"msg","💰");},
      fee_structure_updated:(fs)=>setFeeStruct(fs),
      notice_posted:(n)=>{setNotices(p=>[n,...p]);addToast(`Notice: ${n.tag}`,"msg","📢");},
      notice_deleted:({id})=>setNotices(p=>p.filter(n=>n.id!==id)),
      user_approved:()=>{if(user.role==="admin"||user.role==="teacher")api.getUsers().then(setAllUsers);},
      user_deleted:()=>{if(user.role==="admin"||user.role==="teacher")api.getUsers().then(setAllUsers);},
      users_updated:()=>{if(user.role==="admin"||user.role==="teacher")api.getUsers().then(setAllUsers);},
      home_updated:(d)=>setHomeData(d),
      gallery_updated:()=>api.getHome().then(setHomeData),
      enquiry_received:(e)=>{if(user.role==="admin"){setEnquiries(p=>[e,...p]);addToast(`New enquiry from ${e.name}!`,"msg","📋");}},
    });
    return disc;
  },[user,addToast]);

  const handleLogin=async(u)=>{
    setUser(u);localStorage.setItem("vlearn_user",JSON.stringify(u));
    setScreen("loading");await loadData(u);setScreen("app");
  };
  const handleLogout=()=>{
    localStorage.clear();disconnectSocket();setUser(null);
    setStudents([]);setNotices([]);setAllUsers([]);setEnquiries([]);setScreen("auth");
  };

  return(<>
    <style>{CSS}</style>
    <ToastContainer toasts={toasts}/>
    <InstallPrompt/>

    {screen==="loading"&&(<div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100vh",gap:16}}>
      <VLearnLogo size={80} rounded bg/>
      <div style={{width:"38px",height:"38px",border:"3px solid rgba(255,255,255,.1)",borderTopColor:"var(--gold)",borderRadius:"50%"}} className="spin"></div>
      {loadErr&&<div style={{color:"var(--rose)",fontSize:".82rem",maxWidth:"340px",textAlign:"center",padding:"0 20px"}}>⚠ {loadErr}</div>}
    </div>)}

    {screen==="auth"&&<AuthPage onLogin={handleLogin} onEnquiry={()=>setScreen("enquiry")}/>}
    {screen==="enquiry"&&<EnquiryPage onBack={()=>setScreen("auth")} feeStruct={feeStruct}/>}

    {screen==="app"&&user&&(user.role==="parent"
      ?<ParentApp user={user} feeStruct={feeStruct} homeData={homeData} notices={notices} students={students} onLogout={handleLogout}/>
      :<MainApp user={user} students={students} setStudents={setStudents} notices={notices} setNotices={setNotices} feeStruct={feeStruct} setFeeStruct={setFeeStruct} homeData={homeData} setHomeData={setHomeData} allUsers={allUsers} setAllUsers={setAllUsers} enquiries={enquiries} setEnquiries={setEnquiries} onLogout={handleLogout} addToast={addToast}/>
    )}
  </>);
}
